import { createResource, createSignal, createEffect, createMemo, onMount, onCleanup, Show, For } from 'solid-js';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { currentUser } from '../lib/auth-store.js';
import { useDismissable } from '../lib/dismissable.js';
import {
  gridShowTypes, gridPinPK, gridThousandSep, gridShowHidden,
  setGridShowTypes, setGridPinPK, setGridThousandSep, setGridShowHidden,
  gridLimitEnabled, setGridLimitEnabled, gridRowLimit, setGridRowLimit,
  browserSidebarWidth, setBrowserSidebarWidth,
  browserSidebarCollapsed, setBrowserSidebarCollapsed,
  backupSampleRows,
  editInTransaction, editRequireMode,
} from '../lib/prefs.js';
import { loadColumnPrefs, saveColumnPrefs, resetColumnPrefs } from '../lib/colprefs.js';
import { loadDbPrefs, saveDbPrefs, setDatabaseColor } from '../lib/dbprefs.js';
import { loadBackupModes, setBackupMode } from '../lib/backupprefs.js';
import { loadHiddenDatabases, saveHiddenDatabases } from '../lib/dbvis.js';
import { tableAction } from '../lib/tableactions.js';
import {
  createQuerySession, closeQuerySession,
  beginTransaction, commitTransaction, rollbackTransaction, updateRow, insertRow, deleteRows,
  keyValues, writeValues, sessionLostInfo, sessionLostMessage, deleteConfirmMessage, deleteNote,
  isTransactionalEngine,
} from '../lib/querysession.js';
import Grid, { isNumericType } from '../components/Grid.jsx';
import { buildUpdatePreview, buildInsertPreview } from '../lib/sqlpreview.js';
import { loadCompletionSchema, refreshCompletionSchema, completionState } from '../lib/schema.js';
import { setFunctionDataAccess } from '../lib/routines.js';
import { focusMode } from '../lib/focusmode.js';
import ExportMenu from '../components/ExportMenu.jsx';
import FilterPopover, { formatOp } from '../components/FilterPopover.jsx';
import { cycleSort, pageRange } from '../lib/gridview.js';
import { readClipboardPrefill } from '../lib/export.js';
import ContextMenu from '../components/ContextMenu.jsx';
import PasteObjectsModal from '../components/PasteObjectsModal.jsx';
import { objectClipboard, copyObjectsToClipboard } from '../lib/objectclipboard.js';
import QueryPanel from './QueryPanel.jsx';
import { runningTabs } from '../lib/queryruns.js';
import ScriptEditor from '../components/ScriptEditor.jsx';
import TableEditor from '../components/TableEditor.jsx';
import MySQLUsersPanel from '../components/MySQLUsersPanel.jsx';
import ProcessMonitor from '../components/ProcessMonitor.jsx';
import BackupDialog from '../components/BackupDialog.jsx';
import TableDumpDialog from '../components/TableDumpDialog.jsx';
import { loadTableCreate } from '../lib/tableschema.js';
import { copyText } from '../lib/export.js';
import RestoreDialog from '../components/RestoreDialog.jsx';
import NewObjectModal from '../components/NewObjectModal.jsx';
import ImportDialog from '../components/ImportDialog.jsx';
import DeleteDatabaseDialog from '../components/DeleteDatabaseDialog.jsx';
import MaintainDialog from '../components/MaintainDialog.jsx';
import PKEditConfirmModal from '../components/PKEditConfirmModal.jsx';

// Browser renders the schema tree + inner-tab area for ONE open connection.
// All navigation state (selected DB, open inner tabs) is owned by the parent
// (Shell) and passed in as `state` so it survives connection-tab switching.
//
// Props:
//   connectionId, connectionName
//   state            see Shell.jsx — { database, objects, dataTabs, activeInner }
//   onSelectDB(db)
//   onShowObjects(db, category)
//   onCloseObjects()
//   onOpenTable(db, table)
//   onCloseTable(innerId)
//   onActivateInner(innerId)
export default function Browser(props) {
  const [databases, { refetch: refetchDatabases, mutate: mutateDatabases }] = createResource(
    () => props.connectionId,
    async (id) => {
      try {
        const { databases } = await api.get(`/api/connections/${id}/databases`);
        return databases;
      } catch (e) {
        toast.error(`Load databases: ${e.message}`);
        return [];
      }
    }
  );

  // Server identity (version/flavour) for the sidebar header — one cheap
  // VERSION() round-trip per connection, shown under the connection name.
  const [serverInfo] = createResource(
    () => props.connectionId,
    async (id) => {
      try { return await api.get(`/api/connections/${id}/server-info`); }
      catch { return null; }
    }
  );

  // Preset tints for the per-database color cue (dev/prod tagging) — kept in
  // sync with the connection-level palette in ConnectionForm. Shared across
  // everyone who uses the connection (set via the sidebar DB right-click).
  const DB_COLORS = [
    { value: '',        label: 'None' },
    { value: '#ef4444', label: 'Red (prod)' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#22c55e', label: 'Green' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#a855f7', label: 'Purple' },
    { value: '#6b7280', label: 'Grey' },
  ];
  // Optimistically tint the node, then persist; on failure revert + toast.
  const applyDbColor = async (dbName, color) => {
    const prev = databases() ?? [];
    mutateDatabases(prev.map((d) => d.name === dbName ? { ...d, color } : d));
    try {
      await setDatabaseColor(props.connectionId, dbName, color);
    } catch (e) {
      mutateDatabases(prev); // revert
      toast.error(`Set color: ${e.message}`);
    }
  };

  // ── Sidebar database filter + per-connection visibility ──
  // Quick text filter is session-local; the hidden set is per-(user, conn)
  // and persisted via /database-visibility. Both feed into visibleDatabases().
  const [dbFilter, setDbFilter] = createSignal('');
  const [hiddenDbs, { mutate: mutateHidden }] = createResource(
    () => props.connectionId,
    (id) => loadHiddenDatabases(id),
  );
  const hiddenSet = createMemo(() => new Set(hiddenDbs() ?? []));
  const [visPopoverOpen, setVisPopoverOpen] = createSignal(false);

  // Table editor — modal, not a tab. Local to this Browser instance because
  // it only ever shows one editor at a time and there's no value in
  // surviving connection-tab switches (concurrent edits would be confusing
  // anyway). Shape: { database, table } | null.
  const [editingTable, setEditingTable] = createSignal(null);
  // "New table" — { database } | null. Opens the TableEditor modal in create mode.
  const [creatingTable, setCreatingTable] = createSignal(null);

  // Database backup dialog — { database } | null. Opened from the Objects-tab
  // toolbar or a sidebar database right-click. `dbMenu` is the sidebar DB-node
  // context menu — { x, y, database } | null.
  const [backupTarget, setBackupTarget] = createSignal(null);
  // Restore dialog — { database } | null. Admin-only (destructive).
  const [restoreTarget, setRestoreTarget] = createSignal(null);
  // Import-data dialog — { database, table } | null. RoleUser+ (writes rows).
  const [importTarget, setImportTarget] = createSignal(null);
  // Delete-database dialog — { database } | null. Admin-only (DROP DATABASE).
  const [deleteTarget, setDeleteTarget] = createSignal(null);
  const [dbMenu, setDbMenu] = createSignal(null);
  // Sidebar category (Tables/Views/Functions/Events) right-click menu —
  // { x, y, database, category } | null — and the "new view/event/function"
  // modal target — { database, kind } | null.
  const [catMenu, setCatMenu] = createSignal(null);
  const [newObject, setNewObject] = createSignal(null);
  const isAdmin = () => currentUser()?.role === 'admin';

  // Inner tabs (this connection) bound to a given database — shown as a warning
  // before dropping it. Query tabs follow their last-used DB; the rest are fixed.
  const openTabsFor = (db) => {
    const s = props.state || {};
    const out = [];
    for (const t of s.dataTabs ?? [])      if (t.database === db) out.push(`Data: ${t.table}`);
    for (const t of s.tableEditTabs ?? []) if (t.database === db) out.push(`Edit table: ${t.table}`);
    for (const t of s.scriptTabs ?? [])    if (t.database === db) out.push(`${t.kind}: ${t.name}`);
    for (const t of s.queryTabs ?? [])     if (t.database === db) out.push(`Query tab${t.title ? ` “${t.title}”` : ''}`);
    return out;
  };

  // The inner-tab body element — the "home" parent that query/data tab wrappers
  // live in when not sent to a dual pane. Signal so the relocate effects below
  // re-run once it's mounted.
  const [innerBodyEl, setInnerBodyEl] = createSignal(null);

  // relocateWrap moves a tab's wrapper DOM into its assigned dual-pane slot (or
  // back to the inner body) without unmounting it — so the live MySQL session +
  // result grids survive (Approach B). assign() = { slotEl, resultsOnly } | null.
  const relocateWrap = (el, assign, isActive) => {
    if (!el) return;
    const a = assign();
    const target = a?.slotEl || innerBodyEl();
    if (target && el.parentNode !== target) target.appendChild(el);
    el.style.display = (a || isActive()) ? 'flex' : 'none';
  };

  // Filter the sidebar tree: drop hidden DBs first, then substring-match.
  const visibleDatabases = () => {
    const all = databases() ?? [];
    const hide = hiddenSet();
    const f = dbFilter().trim().toLowerCase();
    return all
      .filter((d) => !hide.has(d.name))
      .filter((d) => !f || d.name.toLowerCase().includes(f));
  };

  // Objects (Tables/Views/Functions/Events) for the currently-expanded DB.
  // Driven by `state.database` so it refreshes when the user expands a different DB.
  const [objects, { refetch: refetchObjects }] = createResource(
    () => (props.state?.database ? [props.connectionId, props.state.database] : null),
    async ([id, db]) => {
      try {
        return await api.get(
          `/api/connections/${id}/databases/${encodeURIComponent(db)}/objects`
        );
      } catch (e) {
        toast.error(`Load objects: ${e.message}`);
        return { tables: [], views: [], functions: [], events: [] };
      }
    }
  );

  // Refresh the Objects data (and the usage counts embedded with it) every
  // time the Objects inner tab becomes active. This guarantees that switching
  // to "Most used" reflects any table opens that happened in between.
  let prevInner = null;
  createEffect(() => {
    const cur = props.state?.activeInner;
    if (cur === 'objects' && prevInner !== 'objects' && props.state?.objects?.open && props.state?.database) {
      refetchObjects();
    }
    prevInner = cur;
  });

  // Drag the splitter on the right edge of the database tree to resize.
  // Width is clamped + persisted by the pref setter.
  const onSidebarDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = browserSidebarWidth();
    const move = (ev) => setBrowserSidebarWidth(startW + (ev.clientX - startX));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div class="browser">
      {/* ── Database tree ── */}
      <aside class={`browser-tree ${browserSidebarCollapsed() ? 'collapsed' : ''}`}
             style={`width: ${browserSidebarWidth()}px;`}>
        <div class="browser-tree-header">
          <span class="mono" style="font-size:11.5px;color:var(--fg-muted);">
            {props.connectionName}
          </span>
          <Show when={serverInfo()?.display}>
            <span class={`server-chip server-chip-${serverInfo()?.flavour || 'mysql'}`}
                  title={`Server: ${serverInfo()?.version || ''}`}>
              {serverInfo().display}
            </span>
          </Show>
        </div>

        {/* Quick filter + visibility funnel. Filter is session-only; visibility
            is persisted per-(user, connection). Funnel opens a popover with
            checkboxes for ALL databases (including hidden ones, so you can
            re-enable them). */}
        <div class="browser-tree-filter">
          <input
            class="input objects-filter-input"
            placeholder="Filter…"
            value={dbFilter()}
            onInput={(e) => setDbFilter(e.target.value)}
          />
          <button
            class={`btn btn-sm btn-ghost db-visibility-btn ${hiddenSet().size > 0 ? 'has-hidden' : ''}`}
            title={hiddenSet().size > 0
              ? `${hiddenSet().size} database(s) hidden — click to manage`
              : 'Manage which databases are visible'}
            onClick={() => setVisPopoverOpen((v) => !v)}
          >
            ⛏
            <Show when={hiddenSet().size > 0}>
              <span class="db-visibility-badge">{hiddenSet().size}</span>
            </Show>
          </button>
        </div>

        <Show when={visPopoverOpen()}>
          <DatabaseVisibilityPopover
            allDatabases={databases() ?? []}
            hiddenSet={hiddenSet()}
            onClose={() => setVisPopoverOpen(false)}
            onSave={async (next) => {
              mutateHidden(next);
              try {
                await saveHiddenDatabases(props.connectionId, next);
              } catch (e) {
                toast.error(`Save visibility: ${e.message}`);
              }
            }}
          />
        </Show>

        <Show when={currentUser()?.role === 'admin'}>
          <div class="tree-section tree-section-fixed">
            <button
              class={`tree-node tree-node-leaf ${props.state?.activeInner === 'mysql-users' ? 'active' : ''}`}
              onClick={() => props.onShowMySQLUsers?.()}
              title="Manage MySQL server users (admin only)"
            >
              <span class="tree-icon">👤</span>
              <span class="tree-label">MySQL users</span>
            </button>
            <button
              class={`tree-node tree-node-leaf ${props.state?.activeInner === 'monitor' ? 'active' : ''}`}
              onClick={() => props.onShowMonitor?.()}
              title="Server / process monitor (admin only)"
            >
              <span class="tree-icon">📊</span>
              <span class="tree-label">Server monitor</span>
            </button>
          </div>
        </Show>

        <div class="tree-section">
          <Show when={databases.loading}>
            <div class="muted" style="padding:8px 10px;">Loading databases…</div>
          </Show>
          <For each={visibleDatabases()}>
            {(d) => (
              <DatabaseNode
                name={d.name}
                color={d.color}
                expanded={props.state?.database === d.name}
                activeCategory={
                  props.state?.database === d.name &&
                  props.state?.objects?.database === d.name &&
                  props.state?.objects?.open &&
                  props.state?.activeInner === 'objects'
                    ? props.state.objects.category
                    : null
                }
                onSelectDB={() => {
                  // Clicking a database opens its Objects tab, reusing the last
                  // selected object type (Tables on first ever open). Switching
                  // DBs keeps you on the same category for the new DB.
                  props.onSelectDB(d.name);
                  props.onShowObjects(d.name, props.state?.objects?.category || 'tables');
                }}
                onSelectCategory={(cat) => {
                  props.onSelectDB(d.name);
                  props.onShowObjects(d.name, cat);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  // Capture Shift at right-click time → forces the simplified
                  // backup/restore flow for this invocation (the ContextMenu
                  // item onClick doesn't carry the event, so we read it here).
                  setDbMenu({ x: e.clientX, y: e.clientY, database: d.name, shift: e.shiftKey });
                }}
                onCategoryMenu={(cat, e) => {
                  if (!isAdmin()) return; // non-admins keep the native menu
                  e.preventDefault();
                  setCatMenu({ x: e.clientX, y: e.clientY, database: d.name, category: cat });
                }}
              />
            )}
          </For>
        </div>
      </aside>

      <Show when={!browserSidebarCollapsed()}>
        <div class="browser-splitter" onPointerDown={onSidebarDrag}
             title="Drag to resize sidebar" />
        {/* Backdrop — only visible on small screens (CSS); tap to close the drawer. */}
        <div class="browser-backdrop" onClick={() => setBrowserSidebarCollapsed(true)} />
      </Show>

      {/* ── Inner tab area ── */}
      <section class="browser-main">
        <div class="browser-topbar">
          {/* Distraction-free mode pins the app logo beside the inner tabs (the
              outer header that normally holds it is hidden). */}
          <Show when={focusMode()}>
            <h1 class="logo browser-focus-logo"><span class="logo-mark" /> Ninocat</h1>
          </Show>
          <button
            class="btn btn-sm btn-ghost sidebar-toggle"
            title={browserSidebarCollapsed() ? 'Show database sidebar' : 'Hide database sidebar'}
            onClick={() => setBrowserSidebarCollapsed(!browserSidebarCollapsed())}
          >☰</button>
          <InnerTabBar
            state={props.state}
            databases={databases()}
            onActivate={props.onActivateInner}
            onCloseObjects={props.onCloseObjects}
            onCloseTable={props.onCloseTable}
            onCloseQuery={props.onCloseQuery}
            onCloseScript={props.onCloseScript}
            onCloseTableEditTab={props.onCloseTableEditTab}
            onOpenQuery={props.onOpenQuery}
            onCloseMySQLUsers={props.onCloseMySQLUsers}
            onCloseMonitor={props.onCloseMonitor}
            onRenameQuery={props.onRenameQuery}
            isAssigned={(innerId) => !!props.assignmentFor?.(innerId)}
            dualPanes={props.dualPanes}
            onSendToDualPane={props.onSendToDualPane}
            onDetach={props.onDetach}
          />
          {/* Focus mode is toggled from the query toolbar's ⛶ button (which flips to
              "Exit focus"); Esc also exits. No separate exit button here. */}
          {/* In focus mode + installed PWA (WCO) this bar IS the title bar; reserve
              the window-controls width so Exit/tabs don't slide under them. */}
          <div class="browser-wco-spacer" />
        </div>

        <div class="inner-tab-body" ref={setInnerBodyEl}>
          {/* Empty state when no inner tabs are open at all */}
          <Show
            when={
              !props.state?.objects?.open &&
              !props.state?.mysqlUsersOpen &&
              !props.state?.monitorOpen &&
              (props.state?.dataTabs?.length ?? 0) === 0 &&
              (props.state?.queryTabs?.length ?? 0) === 0 &&
              (props.state?.scriptTabs?.length ?? 0) === 0 &&
              (props.state?.tableEditTabs?.length ?? 0) === 0
            }
          >
            <div class="empty-state" style="margin:40px auto;max-width:400px;">
              <h2>
                {props.state?.database ? 'Pick a category' : 'Select a database'}
              </h2>
              <p class="muted">
                {props.state?.database
                  ? 'Click Tables, Views, Functions, or Events under the database in the tree.'
                  : 'Expand a database in the tree on the left.'}
              </p>
            </div>
          </Show>

          {/* MySQL users inner-tab body */}
          <Show when={props.state?.mysqlUsersOpen && props.state?.activeInner === 'mysql-users'}>
            <MySQLUsersPanel
              connectionId={props.connectionId}
              connectionName={props.connectionName}
              active={props.state?.activeInner === 'mysql-users'}
            />
          </Show>

          {/* Server monitor inner-tab body */}
          <Show when={props.state?.monitorOpen && props.state?.activeInner === 'monitor'}>
            <ProcessMonitor
              connectionId={props.connectionId}
              connectionName={props.connectionName}
            />
          </Show>

          {/* Objects inner-tab body */}
          <Show
            when={
              props.state?.objects?.open &&
              props.state?.activeInner === 'objects'
            }
          >
            <ObjectsPanel
              connectionId={props.connectionId}
              connectionName={props.connectionName}
              database={props.state.objects.database}
              loading={objects.loading}
              data={objects()}
              category={props.state.objects.category}
              onRefresh={() => refetchObjects()}
              onFindDependencies={props.onFindDependencies}
              filter={props.state?.dbPrefs?.[props.state.objects.database]?.filter ?? ''}
              onSetFilter={(filter) => props.onSetDbPref(props.state.objects.database, { filter })}
              onOpenTable={(name, kind) => props.onOpenTable(props.state.objects.database, name, kind)}
              onOpenScript={(name, kind) => props.onOpenScript?.(props.state.objects.database, kind, name)}
              onOpenTableEdit={(name) => setEditingTable({ database: props.state.objects.database, table: name })}
              onNewTable={() => setCreatingTable({ database: props.state.objects.database })}
              onNewRoutine={(kind) => setNewObject({ database: props.state.objects.database, kind: 'function', forEditor: true, initialRoutineKind: kind })}
              onImport={(name) => setImportTarget({ database: props.state.objects.database, table: name })}
              onOpenTableEditTab={(name) => props.onOpenTableEditTab?.(props.state.objects.database, name)}
              onTableMutated={(name, action) => {
                // Refresh the Objects list (DROP removes the row entirely;
                // empty/truncate leave a stale row count in usage data).
                refetchObjects();
                // DROP also closes any open data tab for the now-gone table.
                if (action === 'drop') {
                  refreshCompletionSchema(props.connectionId, { database: props.state.objects.database });
                  props.onTableDropped?.(props.state.objects.database, name);
                }
              }}
              onRoutineChanged={() => {
                // A function's SQL DATA ACCESS flag changed → refresh the tree (new
                // check mark) and the completion schema (the tx-skip classifier).
                refetchObjects();
                refreshCompletionSchema(props.connectionId, { database: props.state.objects.database });
              }}
            />
          </Show>

          {/* Specific table-data inner tabs. Mounted lazily (on first activate or
              dual-pane assignment) then kept mounted, so a paned data tab stays
              alive while you're on another top-level tab. */}
          <For each={props.state?.dataTabs ?? []}>
            {(t) => {
              const [wrapEl, setWrapEl] = createSignal(null);
              const assign = createMemo(() => props.assignmentFor?.(t.id));
              const isActive = () => props.state?.activeInner === t.id;
              const [ever, setEver] = createSignal(false);
              createEffect(() => { if (assign() || isActive()) setEver(true); });
              createEffect(() => relocateWrap(wrapEl(), assign, isActive));
              return (
                <Show when={ever()}>
                  <div class="query-tab-wrap" ref={setWrapEl}>
                    <TableDataPanel
                      connectionId={props.connectionId}
                      connectionName={props.connectionName}
                      database={t.database}
                      table={t.table}
                      filters={t.filters || []}
                      onSetFilters={(f) => props.onSetTableFilters?.(t.id, f)}
                      onOpenTableFiltered={(db, tbl, f) => props.onOpenTableFiltered?.(db, tbl, f)}
                      onOpenTableEdit={() => setEditingTable({ database: t.database, table: t.table })}
                    />
                  </div>
                </Show>
              );
            }}
          </For>

          {/* Query inner tabs — keep mounted (preserve editor / session) but only
              show the one that's active. Hide via CSS so unmount doesn't kill
              the pinned MySQL session. */}
          <For each={props.state?.queryTabs ?? []}>
            {(t) => {
              const [wrapEl, setWrapEl] = createSignal(null);
              const assign = createMemo(() => props.assignmentFor?.(t.id)); // one scan, shared by effect + props
              const isActive = () => props.state?.activeInner === t.id;
              // Relocate the wrapper into a dual-pane slot (or back) without
              // unmounting QueryPanel — preserves the pinned session + results.
              createEffect(() => relocateWrap(wrapEl(), assign, isActive));
              return (
                <div class="query-tab-wrap" ref={setWrapEl}>
                  <QueryPanel
                    tabId={t.id}
                    connectionId={props.connectionId}
                    initialConnectionId={t.connectionId}
                    connectionName={props.connectionName}
                    initialDatabase={t.database}
                    initialSql={t.sql || ''}
                    initialMessages={t.messages || []}
                    initialSessionId={t.sessionId || ''}
                    initialAutoRun={!!t.autoRun}
                    active={isActive() || !!assign()}
                    compact={!!assign()?.resultsOnly}
                    onOpenQueryWith={(db, sql, autoRun) => props.onOpenQueryWith?.(db, sql, autoRun)}
                    onOpenTableEdit={(db, table) => props.onOpenTableEditTab?.(db, table)}
                    onDatabaseChange={(db) => props.onQueryDatabaseChange?.(t.id, db)}
                    onContentChange={(sql) => props.onQueryContentChange?.(t.id, sql)}
                    onMessagesChange={(msgs) => props.onQueryMessagesChange?.(t.id, msgs)}
                    onSessionIdChange={(sid) => props.onQuerySessionIdChange?.(t.id, sid)}
                    onConnectionChange={(cid) => props.onQueryConnectionChange?.(t.id, cid)}
                  />
                </div>
              );
            }}
          </For>

          {/* Script inner tabs (procedures / functions / views) — also kept
              mounted so unsaved edits aren't lost when switching tabs. */}
          <For each={props.state?.scriptTabs ?? []}>
            {(t) => {
              const [wrapEl, setWrapEl] = createSignal(null);
              const assign = createMemo(() => props.assignmentFor?.(t.id));
              const isActive = () => props.state?.activeInner === t.id;
              createEffect(() => relocateWrap(wrapEl(), assign, isActive));
              return (
                <div class="query-tab-wrap" ref={setWrapEl}>
                  <ScriptEditor
                    connectionId={props.connectionId}
                    connectionName={props.connectionName}
                    database={t.database}
                    kind={t.kind}
                    name={t.name}
                    isNew={!!t.create}
                    seedText={t.seedText}
                    onCreated={(db, k, nm) => {
                      // First save of a new routine: swap this seed tab for the
                      // real routine tab + refresh the object list/autocomplete.
                      props.onCloseScript?.(t.id);
                      props.onOpenScript?.(db, k, nm);
                      refetchObjects();
                      refreshCompletionSchema(props.connectionId, { database: db });
                    }}
                    active={isActive() || !!assign()}
                  />
                </div>
              );
            }}
          </For>

          {/* Full table-editor inner tabs ("Edit Table in Tab"; sendable to a dual pane) */}
          <For each={props.state?.tableEditTabs ?? []}>
            {(t) => {
              const [wrapEl, setWrapEl] = createSignal(null);
              const assign = createMemo(() => props.assignmentFor?.(t.id));
              const isActive = () => props.state?.activeInner === t.id;
              createEffect(() => relocateWrap(wrapEl(), assign, isActive));
              return (
                <div class="query-tab-wrap" ref={setWrapEl}>
                  <TableEditor
                    connectionId={props.connectionId}
                    connectionName={props.connectionName}
                    database={t.database}
                    table={t.table}
                    createMode={!!t.create}
                    onCreated={() => {
                      props.onCloseTableEditTab?.(t.id);
                      refetchObjects();
                      refreshCompletionSchema(props.connectionId, { database: t.database });
                    }}
                    active={isActive() || !!assign()}
                  />
                </div>
              );
            }}
          </For>
        </div>
      </section>

      {/* Table editor — modal so the focused schema operation doesn't share
          attention with browsing tabs. State is local to this Browser; closing
          discards local edits (with a confirm guard if dirty). */}
      <Show when={editingTable()}>
        <TableEditorModal
          connectionId={props.connectionId}
          connectionName={props.connectionName}
          database={editingTable().database}
          table={editingTable().table}
          onClose={() => setEditingTable(null)}
        />
      </Show>

      {/* New table — same editor in create mode (blank spec + editable name). */}
      <Show when={creatingTable()}>
        <TableEditorModal
          connectionId={props.connectionId}
          connectionName={props.connectionName}
          database={creatingTable().database}
          createMode={true}
          onCreated={() => {
            const dbn = creatingTable()?.database;
            setCreatingTable(null); refetchObjects();
            if (dbn) refreshCompletionSchema(props.connectionId, { database: dbn });
          }}
          onClose={() => setCreatingTable(null)}
        />
      </Show>

      {/* Sidebar database right-click menu: Backup (all) + Restore (admin). */}
      <Show when={dbMenu()}>
        <ContextMenu
          x={dbMenu().x}
          y={dbMenu().y}
          items={[
            {
              label: 'Refresh databases',
              icon: '↻',
              onClick: () => refetchDatabases(),
            },
            ...(props.onFindDependencies ? [{
              label: '🧭 Find references (server-wide)',
              onClick: () => props.onFindDependencies({ kind: 'database', name: dbMenu().database, database: dbMenu().database, scope: 'server' }),
            }] : []),
            { divider: true },
            // Shared color cue — tag this DB dev/prod for everyone on the connection.
            {
              swatches: DB_COLORS,
              current: (databases() ?? []).find((d) => d.name === dbMenu().database)?.color || '',
              onPick: (color) => applyDbColor(dbMenu().database, color),
            },
            { divider: true },
            {
              label: 'Backup database…',
              icon: '⬇',
              // Easy mode: per-user flag OR Shift held at right-click. Simplifies
              // the dialog; doesn't change access.
              onClick: () => setBackupTarget({
                database: dbMenu().database,
                easy: !!currentUser()?.easyBackupRestore || !!dbMenu().shift,
              }),
            },
            // Restore items: admin always; user/subadmin with the
            // canRestoreDatabase flag (migration 044); readonly with the
            // separate allowReadonlyRestore flag (migration 045). The two
            // flags don't cross over — a readonly user with canRestoreDb=true
            // still can't restore unless allowReadonlyRestore is also set,
            // and a user/subadmin with allowROR=true still needs canRestoreDb.
            // Mirrors requireRestoreAccess in restore.go; the backend is the
            // authoritative rule. Delete-database stays admin-only.
            ...((isAdmin()
              || (currentUser()?.role !== 'readonly' && !!currentUser()?.canRestoreDatabase)
              || (currentUser()?.role === 'readonly' && !!currentUser()?.allowReadonlyRestore)
            ) ? (
              // Easy mode (per-user flag OR Shift held): only the "restore INTO
              // this database" item — locked to the clicked DB, no create-new,
              // no type-to-confirm. Normal mode keeps both items.
              (!!currentUser()?.easyBackupRestore || !!dbMenu().shift)
                ? [{
                    label: 'Restore into this database…',
                    icon: '⬆',
                    danger: true,
                    onClick: () => setRestoreTarget({ database: dbMenu().database, easy: true }),
                  }]
                : [{
                    label: 'Restore database…',
                    icon: '⬆',
                    danger: true,
                    // Empty target → RestoreDialog opens in "create new database" mode.
                    onClick: () => setRestoreTarget({ database: '' }),
                  }, {
                    label: 'Restore into this database…',
                    icon: '⬆',
                    danger: true,
                    onClick: () => setRestoreTarget({ database: dbMenu().database }),
                  }]
            ) : []),
            ...(isAdmin() ? [{
              label: 'Delete database…',
              icon: '🗑',
              danger: true,
              onClick: () => setDeleteTarget({ database: dbMenu().database }),
            }] : []),
          ]}
          onClose={() => setDbMenu(null)}
        />
      </Show>

      {/* Sidebar category right-click menu — "new object" actions per category. */}
      <Show when={catMenu()}>
        <ContextMenu
          x={catMenu().x}
          y={catMenu().y}
          items={(() => {
            const db = catMenu().database;
            switch (catMenu().category) {
              case 'tables': return [
                { label: 'New table…',       icon: '＋', onClick: () => setCreatingTable({ database: db }) },
                { label: 'New table in a tab', icon: '⊟', onClick: () => props.onOpenNewTableEditTab?.(db) },
              ];
              case 'views':     return [{ label: 'New view…',  icon: '◇', onClick: () => setNewObject({ database: db, kind: 'view' }) }];
              case 'functions': return [{ label: 'New function / procedure…', icon: 'ƒ', onClick: () => setNewObject({ database: db, kind: 'function', forEditor: true }) }];
              case 'events':    return [{ label: 'New event…', icon: '⏱', onClick: () => setNewObject({ database: db, kind: 'event' }) }];
              default: return [];
            }
          })()}
          onClose={() => setCatMenu(null)}
        />
      </Show>

      {/* New object modal. View/event → seed a query tab (onCreate). Functions/
          procedures → open the ScriptEditor seeded with a DROP+CREATE skeleton
          (forEditor / onOpenInEditor). */}
      <Show when={newObject()}>
        <NewObjectModal
          open={true}
          kind={newObject().kind}
          database={newObject().database}
          initialRoutineKind={newObject().initialRoutineKind}
          forEditor={newObject().forEditor}
          onCreate={(sql) => props.onOpenQueryWith?.(newObject().database, sql)}
          onOpenInEditor={({ database, kind, name, sql }) =>
            props.onOpenNewScriptTab?.({ database, kind, name, seedText: sql })}
          onClose={() => setNewObject(null)}
        />
      </Show>

      {/* Per-database backup dialog (object-type toggles → streamed .sql/.sql.gz).
          `easy` hides the option checkboxes (all default on). */}
      <Show when={backupTarget()}>
        <BackupDialog
          connectionId={props.connectionId}
          connectionName={props.connectionName}
          database={backupTarget().database}
          easy={!!backupTarget().easy}
          onClose={() => setBackupTarget(null)}
        />
      </Show>

      {/* Restore dialog — upload a dump → background execute. `easy` drops to a
          minimal existing-DB-only flow locked to the clicked database. */}
      <Show when={restoreTarget()}>
        <RestoreDialog
          connectionId={props.connectionId}
          connectionName={props.connectionName}
          database={restoreTarget().database}
          easy={!!restoreTarget().easy}
          onClose={() => setRestoreTarget(null)}
        />
      </Show>

      {/* Import data dialog (CSV / .xlsx → existing table) — RoleUser+. */}
      <Show when={importTarget()}>
        <ImportDialog
          connectionId={props.connectionId}
          database={importTarget().database}
          table={importTarget().table}
          onOpenInEditor={(db, sql) => { props.onOpenQueryWith?.(db, sql); setImportTarget(null); }}
          onClose={() => setImportTarget(null)}
        />
      </Show>

      {/* Delete-database dialog (admin only) — DROP DATABASE, type-to-confirm. */}
      <Show when={deleteTarget()}>
        <DeleteDatabaseDialog
          connectionId={props.connectionId}
          database={deleteTarget().database}
          openTabs={openTabsFor(deleteTarget().database)}
          onDeleted={() => { refetchDatabases(); loadCompletionSchema(props.connectionId, { force: true }); }}
          onClose={() => setDeleteTarget(null)}
        />
      </Show>
    </div>
  );
}

// Modal wrapper around TableEditor. Backdrop click closes; if the editor is
// dirty the close-handler confirms first (handled via a ref-style callback the
// child sets on mount).
function TableEditorModal(props) {
  // Child registers its "is the form dirty?" accessor here so the wrapper can
  // check it before tearing the modal down on Escape / backdrop click.
  let isDirtyAccessor = () => false;
  const tryClose = () => {
    if (isDirtyAccessor() && !confirm('You have unsaved edits. Discard them and close the editor?')) {
      return;
    }
    props.onClose?.();
  };
  const onKey = (e) => { if (e.key === 'Escape') tryClose(); };
  onMount(() => {
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
  });
  return (
    <div class="modal-backdrop" onMouseDown={(e) => {
      if (e.target.classList?.contains('modal-backdrop')) tryClose();
    }}>
      <div class="modal-card table-editor-modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div class="modal-header">
          <h2>
            <span class="muted mono" style="font-size:12px;">
              {props.connectionName} / {props.database} /
            </span>
            <span class="mono"> {props.createMode ? '(new table)' : props.table}</span>
          </h2>
          <button class="modal-close" onClick={tryClose} aria-label="Close">×</button>
        </div>
        <div class="modal-body table-editor-modal-body">
          <TableEditor
            connectionId={props.connectionId}
            connectionName={props.connectionName}
            database={props.database}
            table={props.table}
            createMode={props.createMode}
            onCreated={props.onCreated}
            active={true}
            registerDirty={(fn) => { isDirtyAccessor = fn; }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Database tree node (expands to 4 category labels) ────────────────────────
function DatabaseNode(props) {
  const CATS = [
    { key: 'tables',    label: 'Tables',    icon: '▤' },
    { key: 'views',     label: 'Views',     icon: '◇' },
    { key: 'functions', label: 'Functions', icon: 'ƒ' },
    { key: 'events',    label: 'Events',    icon: '⏱' },
  ];
  return (
    <div class={`tree-db ${props.expanded ? 'open' : ''}`}
         style={props.color ? `--db-color:${props.color};` : ''}>
      <button
        class={`tree-node tree-node-db ${props.expanded ? 'active' : ''} ${props.color ? 'has-db-color' : ''}`}
        onClick={props.onSelectDB}
        onContextMenu={props.onContextMenu}
        title="Right-click to back up or color-tag this database"
      >
        <span class="caret">{props.expanded ? '▾' : '▸'}</span>
        <span class="tree-icon">⛁</span>
        <span class="tree-label">{props.name}</span>
        <Show when={props.color}>
          <span class="db-color-dot" style={`background:${props.color};`} />
        </Show>
      </button>
      <Show when={props.expanded}>
        <div class="tree-children">
          <For each={CATS}>
            {(c) => (
              <button
                class={`tree-node tree-node-leaf ${
                  props.activeCategory === c.key ? 'active' : ''
                }`}
                onClick={() => props.onSelectCategory(c.key)}
                onContextMenu={(e) => props.onCategoryMenu?.(c.key, e)}
                title="Right-click to create a new object here"
              >
                <span class="tree-icon">{c.icon}</span>
                <span class="tree-label">{c.label}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

// ── Inner tab bar (Objects + data tabs) ──────────────────────────────────────
function InnerTabBar(props) {
  const objectsOpen   = () => !!props.state?.objects?.open;
  const objectsLabel  = () => {
    const c = props.state?.objects?.category || 'tables';
    const db = props.state?.objects?.database || '';
    return `Objects · ${c}${db ? ' · ' + db : ''}`;
  };
  const queryLabel = (t) => {
    const i = (props.state?.queryTabs ?? []).indexOf(t);
    const n = (props.state?.queryTabs ?? []).length - i;  // newest = #1
    return `Query #${n}${t.database ? ' · ' + t.database : ''}`;
  };
  // The per-database color cue (right-click DB → pick color), so a query tab
  // bound to a colored DB carries the same dot. Reuses the sidebar's databases
  // resource + .db-color-dot styling; absent for a switched-connection tab whose
  // DB isn't in this connection's list (no swatch, never a wrong one).
  const dbColor = (dbName) => (props.databases ?? []).find((d) => d.name === dbName)?.color;
  // Single icon glyph per script-kind, kept in sync with ObjectsPanel.
  const SCRIPT_ICON = { procedure: '⚙', function: 'ƒ', view: '◇', event: '⏱' };

  // Inline rename (query tabs) + right-click context menu (all inner tabs).
  const [renaming, setRenaming]     = createSignal(null); // query tab id being renamed
  const [renameText, setRenameText] = createSignal('');
  const [tabMenu, setTabMenu]       = createSignal(null); // { x, y, items }

  const startRename = (t) => {
    setRenameText(t.title || queryLabel(t));
    setRenaming(t.id);
  };
  const commitRename = (t) => {
    if (renaming() !== t.id) return;
    props.onRenameQuery?.(t.id, renameText().trim());
    setRenaming(null);
  };
  const cancelRename = () => setRenaming(null);

  // Flat "Send to dual pane" entries (a disabled header + per-pane Top/Bottom +
  // New) — ContextMenu is flat, so we indent instead of nesting.
  const sendMenuItems = (innerId, kind) => {
    const out = [{ label: 'Send to dual pane:', disabled: true }];
    for (const p of (props.dualPanes || [])) {
      const a = p.orientation === 'lr' ? 'Left' : 'Top';
      const b = p.orientation === 'lr' ? 'Right' : 'Bottom';
      out.push({ label: `    → ${p.title} · ${a}`, onClick: () => props.onSendToDualPane?.(innerId, kind, p.id, 'slot1') });
      out.push({ label: `    → ${p.title} · ${b}`, onClick: () => props.onSendToDualPane?.(innerId, kind, p.id, 'slot2') });
    }
    out.push({ label: '    → New dual pane', onClick: () => props.onSendToDualPane?.(innerId, kind, 'new', 'slot1') });
    return out;
  };

  // Build a Close (+ optional Rename / Send-to-dual-pane) menu for an inner tab.
  const openTabMenu = (e, { rename, close, send }) => {
    e.preventDefault();
    const items = [];
    if (rename) items.push({ label: 'Rename', icon: '✎', onClick: rename });
    items.push({ label: 'Close tab', icon: '✕', onClick: close });
    if (send) {
      items.push({ divider: true });
      if (send.kind === 'query' && runningTabs[send.innerId]) {
        items.push({ label: 'Send to dual pane — stop the query first', disabled: true });
      } else {
        items.push(...sendMenuItems(send.innerId, send.kind));
        if (props.onDetach) {
          items.push({ divider: true });
          items.push({ label: '⧉ Detach as floating panel', onClick: () => props.onDetach(send.innerId, send.kind) });
        }
      }
    }
    setTabMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div class="inner-tab-bar">
      {/* Fixed left cluster — Objects / system tabs + New Query never scroll away. */}
      <div class="inner-tab-fixed">
      <Show when={objectsOpen()}>
        <button
          class="inner-tab inner-tab-pinned"
          classList={{ active: props.state?.activeInner === 'objects' }}
          onClick={() => props.onActivate?.('objects')}
          onContextMenu={(e) => openTabMenu(e, { close: () => props.onCloseObjects?.() })}
          title={objectsLabel()}
        >
          <span class="inner-tab-pin">📌</span>
          <span class="inner-tab-label">{objectsLabel()}</span>
          <span
            class="inner-tab-close"
            onClick={(e) => { e.stopPropagation(); props.onCloseObjects?.(); }}
          >×</span>
        </button>
      </Show>
      <Show when={props.state?.mysqlUsersOpen}>
        <button
          class="inner-tab inner-tab-pinned"
          classList={{ active: props.state?.activeInner === 'mysql-users' }}
          onClick={() => props.onActivate?.('mysql-users')}
          onContextMenu={(e) => openTabMenu(e, { close: () => props.onCloseMySQLUsers?.() })}
          title="MySQL users"
        >
          <span class="inner-tab-icon">👤</span>
          <span class="inner-tab-label">MySQL users</span>
          <span
            class="inner-tab-close"
            onClick={(e) => { e.stopPropagation(); props.onCloseMySQLUsers?.(); }}
          >×</span>
        </button>
      </Show>
      <Show when={props.state?.monitorOpen}>
        <button
          class="inner-tab inner-tab-pinned"
          classList={{ active: props.state?.activeInner === 'monitor' }}
          onClick={() => props.onActivate?.('monitor')}
          onContextMenu={(e) => openTabMenu(e, { close: () => props.onCloseMonitor?.() })}
          title="Server monitor"
        >
          <span class="inner-tab-icon">📊</span>
          <span class="inner-tab-label">Server monitor</span>
          <span
            class="inner-tab-close"
            onClick={(e) => { e.stopPropagation(); props.onCloseMonitor?.(); }}
          >×</span>
        </button>
      </Show>
      {/* "+ New Query" — pinned at the left, right after the Objects/system tabs
          (leftmost if those are all closed), so it doesn't drift right as query
          tabs accumulate. Only when a database is selected (the new tab needs one
          to USE). */}
      <Show when={props.state?.database}>
        <button
          class="inner-tab inner-tab-action inner-tab-newquery"
          onClick={() => props.onOpenQuery?.()}
          title="Open a new query tab (Ctrl+Q)"
        >
          + New Query
        </button>
      </Show>
      </div>
      {/* Scrolling region — document tabs (queries / scripts / table-edit / data).
          Vertical wheel → horizontal scroll so hidden tabs are reachable. */}
      <div
        class="inner-tab-scroll"
        onWheel={(e) => {
          if (e.deltaY === 0) return; // pure horizontal gesture → leave native
          const el = e.currentTarget;
          if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
          el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
          e.preventDefault();
        }}
      >
      {/* Query tabs — render after the New Query button, before data tabs.
          Double-click the label to rename; right-click for Rename / Close. */}
      <For each={props.state?.queryTabs ?? []}>
        {(t) => (
          <Show when={!props.isAssigned?.(t.id)}>
          <button
            class="inner-tab inner-tab-query"
            classList={{ active: props.state?.activeInner === t.id }}
            onClick={() => props.onActivate?.(t.id)}
            onDblClick={() => startRename(t)}
            onContextMenu={(e) => openTabMenu(e, {
              rename: () => startRename(t),
              close: () => props.onCloseQuery?.(t.id),
              send: { innerId: t.id, kind: 'query' },
            })}
            title={t.title || queryLabel(t)}
          >
            <Show when={runningTabs[t.id]} fallback={<span class="inner-tab-icon">⌘</span>}>
              <span class="inner-tab-spinner" title="Query running…" />
            </Show>
            <Show when={dbColor(t.database)}>
              <span class="db-color-dot" style={`background:${dbColor(t.database)};`} title={`Database: ${t.database}`} />
            </Show>
            <Show
              when={renaming() === t.id}
              fallback={<span class="inner-tab-label">{t.title || queryLabel(t)}</span>}
            >
              <input
                class="inner-tab-rename mono"
                value={renameText()}
                ref={(el) => el && setTimeout(() => { el.focus(); el.select(); }, 0)}
                onClick={(e) => e.stopPropagation()}
                onInput={(e) => setRenameText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename(t); }
                  else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                }}
                onBlur={() => commitRename(t)}
              />
            </Show>
            <span
              class="inner-tab-close"
              onClick={(e) => { e.stopPropagation(); props.onCloseQuery?.(t.id); }}
            >×</span>
          </button>
          </Show>
        )}
      </For>
      {/* Script tabs — one per procedure/function/view being edited */}
      <For each={props.state?.scriptTabs ?? []}>
        {(t) => (
          <Show when={!props.isAssigned?.(t.id)}>
          <button
            class="inner-tab inner-tab-script"
            classList={{ active: props.state?.activeInner === t.id }}
            onClick={() => props.onActivate?.(t.id)}
            onContextMenu={(e) => openTabMenu(e, {
              close: () => props.onCloseScript?.(t.id),
              send: { innerId: t.id, kind: 'script' },
            })}
            title={`${t.kind} ${t.database}.${t.name}`}
          >
            <span class="inner-tab-icon">{SCRIPT_ICON[t.kind] || '✎'}</span>
            <span class="inner-tab-label">{t.name}</span>
            <span
              class="inner-tab-close"
              onClick={(e) => { e.stopPropagation(); props.onCloseScript?.(t.id); }}
            >×</span>
          </button>
          </Show>
        )}
      </For>
      {/* Full table-editor inner tabs ("Edit Table in Tab") */}
      <For each={props.state?.tableEditTabs ?? []}>
        {(t) => (
          <Show when={!props.isAssigned?.(t.id)}>
          <button
            class="inner-tab inner-tab-script"
            classList={{ active: props.state?.activeInner === t.id }}
            onClick={() => props.onActivate?.(t.id)}
            onContextMenu={(e) => openTabMenu(e, {
              close: () => props.onCloseTableEditTab?.(t.id),
              send: { innerId: t.id, kind: 'tableedit' },
            })}
            title={t.create ? `New table · ${t.database}` : `Edit table · ${t.database}.${t.table}`}
          >
            <span class="inner-tab-icon">⊟</span>
            <span class="inner-tab-label">{t.create ? `New table · ${t.database}` : `${t.database} → ${t.table}`}</span>
            <span
              class="inner-tab-close"
              onClick={(e) => { e.stopPropagation(); props.onCloseTableEditTab?.(t.id); }}
            >×</span>
          </button>
          </Show>
        )}
      </For>
      <For each={props.state?.dataTabs ?? []}>
        {(t) => (
          <Show when={!props.isAssigned?.(t.id)}>
          <button
            class="inner-tab"
            classList={{ active: props.state?.activeInner === t.id }}
            onClick={() => props.onActivate?.(t.id)}
            onContextMenu={(e) => openTabMenu(e, {
              close: () => props.onCloseTable?.(t.id),
              send: { innerId: t.id, kind: 'data' },
            })}
            title={`${t.database}.${t.table}`}
          >
            <span class="inner-tab-icon">▤</span>
            <span class="inner-tab-label">{t.table}</span>
            <span
              class="inner-tab-close"
              onClick={(e) => { e.stopPropagation(); props.onCloseTable?.(t.id); }}
            >×</span>
          </button>
          </Show>
        )}
      </For>
      </div>
      <Show when={tabMenu()}>
        <ContextMenu
          x={tabMenu().x}
          y={tabMenu().y}
          items={tabMenu().items}
          onClose={() => setTabMenu(null)}
        />
      </Show>
    </div>
  );
}

// ── Objects panel (multi-column horizontal-scroll grid) ──────────────────────
function ObjectsPanel(props) {
  // ── Sort preference: per (user, connection, database), server-persisted ──
  // Filter stays session-only via props.filter / props.onSetFilter from Shell.
  const [dbPrefs, { mutate: mutateDbPrefs }] = createResource(
    () => [props.connectionId, props.database],
    ([id, db]) => loadDbPrefs(id, db)
  );
  const sort = () => dbPrefs()?.objectsSort || 'recent';
  const setSort = (next) => {
    mutateDbPrefs({ objectsSort: next });
    saveDbPrefs(props.connectionId, props.database, { objectsSort: next });
  };

  // Shared per-table backup MODE tags (admin-set): 'structure' | 'sample' |
  // undefined (full). A table→mode map drives the badge + the right-click menu.
  const adminUser = () => currentUser()?.role === 'admin';

  // ＋ New stored-routine dropdown (functions category, admin only). Mirrors the
  // ＋ New table button but offers procedure vs function → NewObjectModal → editor.
  const [newRoutineOpen, setNewRoutineOpen] = createSignal(false);
  let newRoutineBtn, newRoutineMenu;
  useDismissable(() => [newRoutineBtn, newRoutineMenu], () => setNewRoutineOpen(false), { event: 'click' });

  const [backupModes, { refetch: refetchBackupModes }] = createResource(
    // Only the Tables category uses the tags (badge + menu) — don't fetch for
    // Views/Functions/Events.
    () => (props.category === 'tables' ? [props.connectionId, props.database] : null),
    async ([id, db]) => {
      try {
        const { structureOnly, sampleData, skip } = await loadBackupModes(id, db);
        const m = new Map();
        for (const t of structureOnly) m.set(t, 'structure');
        for (const t of sampleData) m.set(t, 'sample');
        for (const t of skip) m.set(t, 'skip');
        return m;
      } catch { return new Map(); }
    },
  );
  const EMPTY_MODES = new Map(); // shared fallback so backupModeOf never allocates per call
  const backupModeOf = (name) => (backupModes() || EMPTY_MODES).get(name);
  const setTableBackupMode = async (name, mode) => {
    try {
      await setBackupMode(props.connectionId, props.database, name, mode);
      refetchBackupModes();
      const msg = mode === 'structure' ? `"${name}" → backup structure only`
        : mode === 'sample' ? `"${name}" → backup sample (first ${backupSampleRows()} rows)`
        : mode === 'skip' ? `"${name}" → do NOT backup`
        : `"${name}" → backup full data`;
      toast.success(msg);
    } catch (e) {
      toast.error(`Tag failed: ${e.message}`);
    }
  };

  // Raw items for the currently selected category. `usage` and `lastUsed` are
  // only present on tables and views (backend embeds them from object_usage).
  const rawItems = () => {
    const d = props.data || {};
    const cat = props.category;
    if (cat === 'tables')
      return (d.tables || []).map((t) => ({
        name: t.name, icon: '▤', subtitle: t.engine || '',
        kind: 'table', usage: t.usage || 0, lastUsed: t.lastUsed || 0,
      }));
    if (cat === 'views')
      return (d.views || []).map((v) => ({
        name: v.name, icon: '◇', subtitle: '',
        kind: 'view', usage: v.usage || 0, lastUsed: v.lastUsed || 0,
      }));
    if (cat === 'functions')
      return (d.functions || []).map((f) => {
        // The "functions" category from the backend contains BOTH stored
        // functions and procedures (information_schema.ROUTINES groups them).
        // f.type is "PROCEDURE" or "FUNCTION" — pick an icon to match.
        const isProc = (f.type || '').toUpperCase() === 'PROCEDURE';
        return {
          name: f.name,
          icon: isProc ? '⚙' : 'ƒ',
          subtitle: f.type || '',
          kind:    isProc ? 'procedure' : 'function',
          dataAccess: f.dataAccess || '', // SQL DATA ACCESS — drives the right-click flag
        };
      });
    if (cat === 'events')
      return (d.events || []).map((e) => ({
        name: e.name, icon: '⏱', subtitle: e.status || '', kind: 'event',
      }));
    return [];
  };

  // Whether the current category has usage tracking (tables + views only).
  const sortable = () => props.category === 'tables' || props.category === 'views';

  // Per-category click behaviour. See the call-site for the wiring; the spec is:
  //   tables       — single-click   = open data tab.
  //   views        — double-click   = open data tab (run);
  //                  right-click    = open script editor.
  //   functions    — double-click   = open script editor.
  //   procedures   — double-click   = open script editor.
  // (Single-click on views/functions/procs does nothing; matches the user's
  // explicit choice to require an unambiguous action for editable objects.)
  const cat       = () => props.category;

  // ── Multi-select for the object clipboard (copy/paste across connections) ──
  // Keyed `kind:name` so the selection can span category tabs; cleared when the
  // connection or database changes. Plain click selects one (and keeps the
  // existing open-on-click for tables); Ctrl/Cmd-click toggles; Shift-click
  // range-selects within the current category. See OBJECT_COPY_SPEC.md.
  const objKey = (it) => `${it.kind}:${it.name}`;
  const [selected, setSelected] = createSignal(new Set());
  let selAnchor = -1; // index in visibleItems() for shift-range
  createEffect(() => { props.connectionId; props.database; setSelected(new Set()); selAnchor = -1; });
  const isSelected = (it) => selected().has(objKey(it));
  const idxOf = (it) => visibleItems().findIndex((x) => objKey(x) === objKey(it));
  const toggleSel = (it) => setSelected((s) => {
    const n = new Set(s); const k = objKey(it);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });
  const rangeSel = (it) => {
    const items = visibleItems();
    const to = idxOf(it);
    const from = selAnchor < 0 ? to : selAnchor;
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    setSelected((s) => {
      const n = new Set(s);
      for (let i = lo; i <= hi; i++) if (items[i]) n.add(objKey(items[i]));
      return n;
    });
  };

  let gridEl; // focusable container so F2 / Ctrl+C / Ctrl+V work after a click
  const itemClick = (it, e) => {
    gridEl?.focus({ preventScroll: true }); // buttons don't focus on click in FF/Safari
    if (e.shiftKey) { e.preventDefault(); rangeSel(it); return; }
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); toggleSel(it); selAnchor = idxOf(it); return; }
    // Plain click only SELECTS now — open via double-click, Enter, or right-click.
    setSelected(new Set([objKey(it)]));
    selAnchor = idxOf(it);
  };
  // Open an object: tables/views → data tab; routines/events → script editor.
  const openObject = (it) => {
    if (it.kind === 'table' || it.kind === 'view') props.onOpenTable?.(it.name, it.kind);
    else if (it.kind === 'procedure' || it.kind === 'function') props.onOpenScript?.(it.name, it.kind);
    else if (it.kind === 'event') props.onOpenScript?.(it.name, 'event');
  };

  // ── Object clipboard: copy the selection, paste into this (conn, db) ──
  const [pasteOpen, setPasteOpen] = createSignal(false);
  const doCopy = () => {
    const items = [...selected()].map((k) => {
      const i = k.indexOf(':');
      return { kind: k.slice(0, i), name: k.slice(i + 1) };
    });
    if (!items.length) { toast.error('No objects selected.'); return; }
    copyObjectsToClipboard({
      sourceConnId: props.connectionId, sourceConnName: props.connectionName,
      sourceDb: props.database, items,
    });
    toast.success(`Copied ${items.length} object${items.length === 1 ? '' : 's'} to the object clipboard.`);
  };
  // Copy/Paste menu entries appended to every object's context menu + the empty area.
  const copyPasteItems = () => {
    const n = selected().size;
    const out = [{ label: n > 1 ? `📋 Copy (${n} objects)` : '📋 Copy', onClick: doCopy }];
    const cbd = objectClipboard();
    if (cbd) out.push({ label: `📥 Paste ${cbd.items.length} object${cbd.items.length === 1 ? '' : 's'}…`, onClick: () => setPasteOpen(true) });
    return out;
  };
  const itemDblClick = (it) => openObject(it);
  // Right-click context menu — populated per category.
  const [tableCtx, setTableCtx] = createSignal(null); // { x, y, items }
  const [maintainTarget, setMaintainTarget] = createSignal(null); // table name | null

  const itemContextMenu = (it, e) => {
    e.preventDefault();
    e.stopPropagation(); // don't also fire the grid's empty-area (paste) menu
    // Right-clicking outside the current selection re-selects just this object, so
    // Copy acts on what was clicked; right-clicking inside it keeps the selection.
    if (!isSelected(it)) { setSelected(new Set([objKey(it)])); selAnchor = idxOf(it); }
    const renameItem = { label: '✎ Rename…', onClick: () => openRenameDialog(it) };
    // Dependency Analyzer entry points (open the Tools panel pre-filled + run).
    const depsItems = props.onFindDependencies ? [
      { label: '🧭 Find dependencies (schema)', onClick: () => props.onFindDependencies({ database: props.database, kind: it.kind, name: it.name, scope: 'schema' }) },
      { label: '🧭 Find dependencies (server-wide)', onClick: () => props.onFindDependencies({ database: props.database, kind: it.kind, name: it.name, scope: 'server' }) },
    ] : [];
    let items;
    if (cat() === 'tables') {
      // Tables: Open table (top), Rename…, Edit Table, then the rest, then copy/paste.
      items = [
        { label: '▤ Open table', onClick: () => openObject(it) },
        renameItem,
        ...tableMenuItems(it),
        { divider: true },
        ...depsItems,
        ...copyPasteItems(),
      ];
    } else {
      let base = [];
      if (cat() === 'views') base = [
        { label: '▷ Open view', onClick: () => props.onOpenTable?.(it.name, it.kind) },
        { label: 'Edit script', icon: '✎', onClick: () => props.onOpenScript?.(it.name, 'view') },
      ];
      else if (cat() === 'functions') base = it.kind === 'function'
        ? functionMenuItems(it)
        : [{ label: 'Edit script', icon: '✎', onClick: () => props.onOpenScript?.(it.name, 'procedure') }];
      else if (cat() === 'events') base = [
        { label: 'Edit script', icon: '✎', onClick: () => props.onOpenScript?.(it.name, 'event') },
      ];
      items = [...base, { divider: true }, renameItem, ...depsItems, ...copyPasteItems()];
    }
    setTableCtx({ x: e.clientX, y: e.clientY, items });
  };

  // Flag a stored function as modifying / read-only via its MySQL SQL DATA ACCESS
  // characteristic (admin-only on the backend). The check mark shows the current
  // value; READS SQL DATA / NO SQL both count as "read-only" for the optimization.
  const setFnAccess = async (it, access) => {
    try {
      await setFunctionDataAccess(props.connectionId, props.database, it.name, access);
      toast.success(`"${it.name}" → ${access === 'MODIFIES SQL DATA' ? 'modifies data' : 'read-only'}`);
      props.onRoutineChanged?.(); // refresh the tree + the tx-skip classifier metadata
    } catch (e) {
      toast.error(`Flag failed: ${e.message}`);
    }
  };
  const functionMenuItems = (it) => {
    const cur = (it.dataAccess || '').toUpperCase();
    const isModifying = cur === 'MODIFIES SQL DATA';
    const isReadOnly  = cur === 'READS SQL DATA' || cur === 'NO SQL';
    return [
      { label: 'Edit script', icon: '✎', onClick: () => props.onOpenScript?.(it.name, 'function') },
      { divider: true },
      { label: 'Modifies data',          icon: isModifying ? '✓' : undefined, onClick: () => setFnAccess(it, 'MODIFIES SQL DATA') },
      { label: 'Read-only (reads data)', icon: isReadOnly  ? '✓' : undefined, onClick: () => setFnAccess(it, 'READS SQL DATA') },
    ];
  };

  // Destructive table actions. Confirmation uses native confirm() with an
  // explicit type-the-table-name guard for DROP, since it can't be undone.
  const runTableAction = async (action, name, prompt) => {
    if (!confirm(prompt)) return;
    try {
      await tableAction(props.connectionId, props.database, name, action);
      const verb = action === 'empty' ? 'emptied'
                 : action === 'truncate' ? 'truncated'
                 : 'dropped';
      toast.success(`Table "${name}" ${verb}`);
      props.onTableMutated?.(name, action);
    } catch (e) {
      toast.error(`${action} failed: ${e.message}`);
    }
  };

  // Copy SHOW CREATE TABLE to the clipboard. Best-effort: surfaces a clear
  // toast on both success and failure (insecure-context + permission gotchas
  // hide in copyText, which already handles the http-context fallback).
  const copyTableSchema = async (name) => {
    try {
      const r = await loadTableCreate(props.connectionId, props.database, name);
      if (!r?.sql) {
        toast.error('Server returned an empty CREATE TABLE.');
        return;
      }
      await copyText(r.sql);
      toast.success(`Schema for "${name}" copied to clipboard.`);
    } catch (e) {
      toast.error(`Copy schema failed: ${e?.message || e}`);
    }
  };

  // Dump-table-dialog target — null when closed; { table } when open.
  const [tableDumpTarget, setTableDumpTarget] = createSignal(null);

  // Build the right-click menu for a table. "New Table" is still a disabled
  // placeholder (create flow TBD); "Maintain Table…" (admin) opens the
  // check/analyze/optimize/repair dialog.
  const tableMenuItems = (it) => [
    { label: 'Edit Table', icon: '✎',
      onClick: () => props.onOpenTableEdit?.(it.name) },
    { label: 'New Table',         icon: '＋', onClick: () => props.onNewTable?.() },
    { label: 'Edit Table in Tab', icon: '⊟', onClick: () => props.onOpenTableEditTab?.(it.name) },
    { divider: true },
    // Copy the server's own SHOW CREATE TABLE output to the clipboard — the
    // version-correct DDL ("Copy Schema (SQL)"). Available to anyone with
    // table access; the endpoint is read-only schema metadata.
    { label: 'Copy Schema (SQL)', icon: '📋', onClick: () => copyTableSchema(it.name) },
    // Stream a single-table dump (CREATE + INSERTs, or data-only) as a .sql /
    // .sql.gz download. Same access model — anyone who can read the rows.
    { label: 'Dump Table into SQL…', icon: '💾', onClick: () => setTableDumpTarget({ table: it.name }) },
    ...(currentUser()?.role !== 'readonly' ? [
      { label: 'Import data…', icon: '⬆', onClick: () => props.onImport?.(it.name) },
    ] : []),
    ...(adminUser() ? [
      { label: 'Maintain Table…', icon: '⚒', onClick: () => setMaintainTarget(it.name) },
    ] : []),
    ...(adminUser() ? (() => {
      const mode = backupModeOf(it.name);
      const tick = (m) => (mode === m || (m === 'none' && !mode)) ? ' ✓' : '';
      return [
        { divider: true },
        { label: 'Backup: full data' + tick('none'), icon: '▦',
          onClick: () => setTableBackupMode(it.name, 'none') },
        { label: 'Backup: structure only' + tick('structure'), icon: '⊘',
          onClick: () => setTableBackupMode(it.name, 'structure') },
        { label: `Backup: sample (${backupSampleRows()} rows)` + tick('sample'), icon: '◔',
          onClick: () => setTableBackupMode(it.name, 'sample') },
        { label: 'Do not backup' + tick('skip'), icon: '⊗', danger: true,
          onClick: () => setTableBackupMode(it.name, 'skip') },
      ];
    })() : []),
    { divider: true },
    {
      label: 'Empty Table', icon: '∅', danger: true,
      onClick: () => runTableAction('empty', it.name,
        `Empty table "${it.name}"?\n\nThis will DELETE all rows. Auto-increment counter is preserved. This cannot be undone.`),
    },
    {
      label: 'Truncate Table', icon: '✂', danger: true,
      onClick: () => runTableAction('truncate', it.name,
        `Truncate table "${it.name}"?\n\nThis will remove all rows and reset the auto-increment counter. This cannot be undone.`),
    },
    {
      label: 'Delete Table', icon: '🗑', danger: true,
      onClick: () => runTableAction('drop', it.name,
        `DROP table "${it.name}"?\n\nThis will permanently remove the table and all its data. This cannot be undone.`),
    },
  ];

  // Whether the tile should render as enabled (not greyed out).
  const enabled = (it) =>
    cat() === 'tables' || cat() === 'views' || cat() === 'events' ||
    (cat() === 'functions' && (it.kind === 'function' || it.kind === 'procedure'));

  const byAlpha = (a, b) => a.name.localeCompare(b.name);

  // Apply the quick filter (case-insensitive substring), then the selected sort.
  const visibleItems = () => {
    const f = (props.filter || '').trim().toLowerCase();
    let arr = rawItems();
    if (f) arr = arr.filter((it) => it.name.toLowerCase().includes(f));

    const s = sort();
    if (s === 'usage' && sortable()) {
      // Used items by count desc; then never-used alphabetically.
      const used   = arr.filter((it) => (it.usage || 0) > 0)
                         .sort((a, b) => b.usage - a.usage || byAlpha(a, b));
      const unused = arr.filter((it) => !it.usage).sort(byAlpha);
      return [...used, ...unused];
    }
    if (s === 'recent' && sortable()) {
      // Recently-opened items by lastUsed desc; then never-opened alphabetically.
      const seen   = arr.filter((it) => (it.lastUsed || 0) > 0)
                         .sort((a, b) => b.lastUsed - a.lastUsed || byAlpha(a, b));
      const unseen = arr.filter((it) => !it.lastUsed).sort(byAlpha);
      return [...seen, ...unseen];
    }
    return [...arr].sort(byAlpha);
  };

  const totalCount    = () => rawItems().length;
  const filteredCount = () => visibleItems().length;
  const isFiltered    = () => (props.filter || '').trim() !== '';

  // ── Rename an object (F2 = in-place; right-click → Rename… = dialog) ──
  const [renaming, setRenaming] = createSignal(null);   // objKey being inline-renamed
  const [renameDraft, setRenameDraft] = createSignal('');
  let renameCancel = false;
  const [renameDialog, setRenameDialog] = createSignal(null); // { it, draft } | null
  // Objects that reference the one being renamed (rename dialog warning).
  const [renameDeps] = createResource(
    () => (renameDialog() ? [renameDialog().it.kind, renameDialog().it.name] : null),
    async ([kind, name]) => {
      try {
        const r = await api.post(`/api/connections/${props.connectionId}/databases/${encodeURIComponent(props.database)}/objects/dependents`, { kind, name });
        return r.dependents || [];
      } catch { return []; }
    },
  );
  const renameDepsNonFk = () => (renameDeps() || []).filter((d) => d.kind !== 'fk');
  const renameDepsFk = () => (renameDeps() || []).filter((d) => d.kind === 'fk');
  const doRename = async (it, newName) => {
    const nn = (newName || '').trim();
    if (!nn || nn === it.name) return;
    try {
      await api.post(`/api/connections/${props.connectionId}/databases/${encodeURIComponent(props.database)}/objects/rename`,
        { kind: it.kind, name: it.name, newName: nn });
      toast.success(`Renamed “${it.name}” → “${nn}”`);
      setSelected(new Set());
      props.onRefresh?.();
    } catch (e) { toast.error('Rename failed: ' + (e?.message || e)); }
  };
  const startInlineRename = (it) => { setRenameDraft(it.name); renameCancel = false; setRenaming(objKey(it)); };
  const finishInlineRename = (it) => {
    if (renaming() !== objKey(it)) return;
    const draft = renameDraft();
    const cancel = renameCancel;
    setRenaming(null);
    if (!cancel) doRename(it, draft);
  };
  const openRenameDialog = (it) => setRenameDialog({ it, draft: it.name });
  const submitRenameDialog = () => { const d = renameDialog(); if (d) { setRenameDialog(null); doRename(d.it, d.draft); } };

  // F2 (rename) + Ctrl+C / Ctrl+V on the object list — only while focus is inside
  // this panel, and never while typing in an input (the rename box / filter).
  let rootEl;
  onMount(() => {
    const onKey = (e) => {
      if (!rootEl || !rootEl.contains(document.activeElement)) return;
      const ae = document.activeElement;
      const editable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      if (e.key === 'F2' && !editable && renaming() === null && selected().size === 1) {
        e.preventDefault();
        const it = visibleItems().find((x) => objKey(x) === [...selected()][0]);
        if (it) startInlineRename(it);
        return;
      }
      // Enter — open the single selected object (the keyboard equivalent of dblclick).
      if (e.key === 'Enter' && !editable && renaming() === null && selected().size === 1
          && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        const it = visibleItems().find((x) => objKey(x) === [...selected()][0]);
        if (it) openObject(it);
        return;
      }
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl || editable) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'c' && selected().size > 0) { e.preventDefault(); doCopy(); }
      else if (k === 'v' && objectClipboard()) { e.preventDefault(); setPasteOpen(true); }
    };
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
  });

  return (
    <div class="objects-panel" ref={rootEl}>
      <div class="objects-toolbar">
        <span class="muted mono" style="font-size:12px;">
          {props.category} · {isFiltered()
            ? `${filteredCount()} of ${totalCount()}`
            : `${totalCount()} item${totalCount() === 1 ? '' : 's'}`}
        </span>

        <Show when={isFiltered()}>
          <span class="objects-filter-hint">
            Filtered by “{props.filter}” —{' '}
            <button class="objects-filter-clearlink" onClick={() => props.onSetFilter?.('')}>
              clear
            </button>
          </span>
        </Show>

        {/* Controls pushed to the right: New table first, then filter, then sort. */}
        <div class="objects-toolbar-right">
          <Show when={cat() === 'tables' && adminUser()}>
            <button
              class="btn btn-sm btn-ghost"
              title="Create a new table in this database"
              onClick={() => props.onNewTable?.()}
            >＋ New table</button>
          </Show>

          {/* New stored procedure / function → params dialog → script editor. */}
          <Show when={cat() === 'functions' && adminUser()}>
            <div class="export-menu">
              <button
                ref={newRoutineBtn}
                class="btn btn-sm btn-ghost"
                title="Create a new stored procedure or function"
                onClick={() => setNewRoutineOpen(!newRoutineOpen())}
              >＋ New ▾</button>
              <Show when={newRoutineOpen()}>
                <div ref={newRoutineMenu} class="export-menu-pop">
                  <button class="export-menu-item" onClick={() => { setNewRoutineOpen(false); props.onNewRoutine?.('procedure'); }}>Stored procedure</button>
                  <button class="export-menu-item" onClick={() => { setNewRoutineOpen(false); props.onNewRoutine?.('function'); }}>Stored function</button>
                </div>
              </Show>
            </div>
          </Show>

          {/* Quick filter input with × clear button (session-only) */}
          <div class="objects-filter-wrap" classList={{ active: isFiltered() }}>
            <span class="objects-filter-icon">⌕</span>
            <input
              class="objects-filter-input"
              type="text"
              placeholder="Filter…"
              value={props.filter || ''}
              onInput={(e) => props.onSetFilter?.(e.target.value)}
            />
            <Show when={isFiltered()}>
              <button
                class="objects-filter-clear"
                title="Clear filter"
                onClick={() => props.onSetFilter?.('')}
              >×</button>
            </Show>
          </div>

          {/* Sort dropdown — persists per (user, connection, database) */}
          <label class="objects-sort">
            <span class="muted" style="font-size:11.5px;">Sort:</span>
            <select
              class="input objects-sort-select"
              value={sort()}
              onChange={(e) => setSort(e.target.value)}
              disabled={!sortable()}
              title={sortable() ? '' : 'Usage tracking only applies to Tables and Views'}
            >
              <option value="alpha"  selected={sort() === 'alpha'}>Alphabetical</option>
              <option value="usage"  selected={sort() === 'usage'}>Most used</option>
              <option value="recent" selected={sort() === 'recent'}>Recently used</option>
            </select>
          </label>
        </div>
      </div>

      <Show when={props.loading}>
        <div class="muted" style="padding:16px;">Loading…</div>
      </Show>
      <Show when={!props.loading}>
        <Show when={visibleItems().length === 0} fallback={
          <div
            class="objects-grid"
            ref={gridEl}
            tabindex={-1}
            onContextMenu={(e) => {
              // Empty-area right-click → Paste (item right-clicks stopPropagation).
              const cbd = objectClipboard();
              if (!cbd) return;
              e.preventDefault();
              setTableCtx({ x: e.clientX, y: e.clientY, items: [
                { label: `📥 Paste ${cbd.items.length} object${cbd.items.length === 1 ? '' : 's'}…`, onClick: () => setPasteOpen(true) },
              ] });
            }}
            onWheel={(e) => {
              // This list wraps into columns and scrolls horizontally only
              // (overflow-y hidden), so a plain vertical wheel would do nothing.
              // Translate it into horizontal scroll — no Shift needed.
              if (e.deltaY === 0) return;                  // pure horizontal gesture → leave native
              const el = e.currentTarget;
              if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
              el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY; // line-mode → ~px
              e.preventDefault();
            }}
          >
            <For each={visibleItems()}>
              {(it) => (
                <Show when={renaming() === `${it.kind}:${it.name}`} fallback={
                <button
                  class="objects-item"
                  classList={{ selected: isSelected(it) }}
                  disabled={!enabled(it)}
                  onClick={(e) => enabled(it) && itemClick(it, e)}
                  onDblClick={() => enabled(it) && itemDblClick(it)}
                  onContextMenu={(e) => enabled(it) && itemContextMenu(it, e)}
                  title={(() => {
                    const base = it.subtitle ? `${it.name} (${it.subtitle})` : it.name;
                    if (cat() === 'tables')    return `${base} — double-click (or Enter) to open · F2 to rename · right-click for actions`;
                    if (cat() === 'views')     return `${base} — double-click to open · right-click to edit`;
                    if (cat() === 'functions') return `${base} — double-click to edit script`;
                    if (cat() === 'events')    return `${base} — double-click to edit script`;
                    return base;
                  })()}
                >
                  <span class="objects-item-icon">{it.icon}</span>
                  <span class="objects-item-name">{it.name}</span>
                  <Show when={cat() === 'tables' && backupModeOf(it.name) === 'structure'}>
                    <span class="objects-item-badge" title="Backup: structure only (rows skipped)">⊘</span>
                  </Show>
                  <Show when={cat() === 'tables' && backupModeOf(it.name) === 'sample'}>
                    <span class="objects-item-badge" title={`Backup: sample (first ${backupSampleRows()} rows)`}>◔</span>
                  </Show>
                  <Show when={cat() === 'tables' && backupModeOf(it.name) === 'skip'}>
                    <span class="objects-item-badge objects-item-badge-skip" title="Do not backup (excluded)">⊗</span>
                  </Show>
                </button>
                }>
                  <input
                    class="objects-item objects-item-rename mono"
                    value={renameDraft()}
                    ref={(el) => queueMicrotask(() => { el?.focus(); el?.select(); })}
                    onInput={(e) => setRenameDraft(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); renameCancel = false; e.currentTarget.blur(); }
                      else if (e.key === 'Escape') { e.preventDefault(); renameCancel = true; e.currentTarget.blur(); }
                    }}
                    onBlur={() => finishInlineRename(it)}
                  />
                </Show>
              )}
            </For>
          </div>
        }>
          <div class="muted" style="padding:16px;">
            {isFiltered() ? `No ${props.category} match the filter.` : `No ${props.category}.`}
          </div>
        </Show>
      </Show>

      <Show when={tableCtx()}>
        <ContextMenu
          x={tableCtx().x}
          y={tableCtx().y}
          items={tableCtx().items}
          onClose={() => setTableCtx(null)}
        />
      </Show>

      <Show when={maintainTarget()}>
        <MaintainDialog
          connectionId={props.connectionId}
          database={props.database}
          table={maintainTarget()}
          onClose={() => setMaintainTarget(null)}
        />
      </Show>

      {/* Single-table SQL dump dialog (right-click → Dump Table into SQL…). */}
      <Show when={tableDumpTarget()}>
        <TableDumpDialog
          connectionId={props.connectionId}
          database={props.database}
          table={tableDumpTarget().table}
          onClose={() => setTableDumpTarget(null)}
        />
      </Show>

      {/* Paste objects from the clipboard into THIS (connection, database). */}
      <Show when={pasteOpen() && objectClipboard()}>
        <PasteObjectsModal
          clipboard={objectClipboard()}
          target={{ connectionId: props.connectionId, connectionName: props.connectionName, database: props.database }}
          onClose={() => setPasteOpen(false)}
          onDone={() => props.onRefresh?.()}
        />
      </Show>

      {/* Rename dialog (right-click → Rename…). */}
      <Show when={renameDialog()}>
        <div class="modal-backdrop" onClick={() => setRenameDialog(null)}>
          <div class="modal-card" style="width:min(440px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3>Rename {renameDialog().it.kind}</h3>
            <p class="muted" style="font-size:12px;margin:0 0 10px;">
              <span class="mono">{renameDialog().it.name}</span> → a new name in <span class="mono">{props.database}</span>.
            </p>
            <input class="input mono" style="width:100%;" value={renameDialog().draft} autofocus
                   onInput={(e) => setRenameDialog({ ...renameDialog(), draft: e.currentTarget.value })}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') { e.preventDefault(); submitRenameDialog(); }
                     else if (e.key === 'Escape') { e.preventDefault(); setRenameDialog(null); }
                   }} />

            {/* Dependency scan can be slow on a big schema (it scans routine bodies). */}
            <Show when={renameDeps.loading}>
              <div class="muted dep-checking" style="margin-top:10px;">
                <span class="dep-spinner" /> Checking dependencies…
              </div>
            </Show>

            {/* Dependents warning — objects that reference the one being renamed. */}
            <Show when={renameDepsNonFk().length}>
              <div class="import-warn import-warn-soft" style="margin-top:10px;">
                <strong>{renameDepsNonFk().length} object{renameDepsNonFk().length === 1 ? '' : 's'} reference “{renameDialog().it.name}”</strong> by name and won’t be updated automatically:
                <ul style="margin:6px 0 0;padding-left:18px;max-height:120px;overflow:auto;">
                  <For each={renameDepsNonFk()}>
                    {(dep) => <li><span class="mono muted">{dep.kind}</span> <span class="mono">{dep.name}</span>{dep.detail ? <span class="muted"> · {dep.detail}</span> : null}</li>}
                  </For>
                </ul>
              </div>
            </Show>
            <Show when={renameDepsFk().length}>
              <div class="muted" style="font-size:12px;margin-top:6px;">
                {renameDepsFk().length} foreign-key child table{renameDepsFk().length === 1 ? '' : 's'} reference{renameDepsFk().length === 1 ? 's' : ''} it — these update automatically.
              </div>
            </Show>

            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
              <button class="btn btn-ghost" onClick={() => setRenameDialog(null)}>Cancel</button>
              <button class="btn" disabled={!renameDialog().draft.trim() || renameDialog().draft.trim() === renameDialog().it.name}
                      onClick={submitRenameDialog}>Rename</button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}

// ── Table data panel (paginated grid + column filters) ──────────────────────
function TableDataPanel(props) {
  const [page, setPage] = createSignal(0);

  // Row limit (Navicat-style): the checkbox + value control the page size. When
  // unchecked, fetch up to MAX_ROWS per page (the backend caps at this too), so
  // you can still page through a huge table without trying to load it all at
  // once. Persisted per user via the grid.limitEnabled / grid.rowLimit prefs.
  const MAX_ROWS = 100000;
  const pageSize = createMemo(() => {
    if (!gridLimitEnabled()) return MAX_ROWS;
    const n = Math.floor(Number(gridRowLimit()));
    return Math.min(MAX_ROWS, Math.max(1, Number.isFinite(n) ? n : 1000));
  });

  // Filter popover state (which header was clicked, where it was anchored, and
  // an optional value prefilled from the clipboard on header click).
  const [popoverCol, setPopoverCol]   = createSignal(null);
  const [popoverRect, setPopoverRect] = createSignal(null);
  const [prefillValue, setPrefillValue] = createSignal('');
  const [prefillOp, setPrefillOp]     = createSignal(undefined); // initial operator for the popover
  const [editIndex, setEditIndex]     = createSignal(null);      // null = add a new filter; number = edit that filter

  // Convenience accessor — filters live in Shell so they persist.
  const filters = () => props.filters || [];

  // Multi-column sort — session-local. Each entry: { column, dir: 'asc'|'desc' }.
  // Cleared when the user navigates to a different table.
  const [sortKeys, setSortKeys] = createSignal([]);

  // ── Edit mode ──
  // Edit mode opens a pinned MySQL session, starts a transaction, and every
  // cell change fires a structured UPDATE on it. The SQL log shows what
  // ran. Commit / Rollback both restart a fresh tx automatically so the
  // editor is always in a state where the next edit has a tx to attach to.
  const [editMode,  setEditMode]  = createSignal(false);
  const [sessionId, setSessionId] = createSignal(null);
  const [inTx,      setInTx]      = createSignal(false);
  const [sqlLog,    setSqlLog]    = createSignal([]); // { sql, at, ok, error?, rowsAffected }
  const [logSplit,  setLogSplit]  = createSignal(0.65); // editor area / log split ratio
  let splitHostEl;

  const logPush = (entry) => setSqlLog((arr) => [...arr, { ...entry, at: Date.now() }]);
  const logClear = () => setSqlLog([]);

  // Live "would-run" UPDATE preview while editing a row — updates as the user
  // types / moves between columns, before the row is sent (shown atop the SQL
  // log). The empty string hides it. Matters most on a single filtered row,
  // where there's no other row to move to so the write isn't sent until Commit.
  const [editPreview, setEditPreview] = createSignal('');
  const [showPreviewModal, setShowPreviewModal] = createSignal(false);
  // Pending INSERTs for new draft rows are shown as their own list (see
  // pendingInserts / onDraftsChange), so the single focused-row preview is for
  // UPDATEs only.
  const [pendingInserts, setPendingInserts] = createSignal([]);
  const handleDraftsChange = (list) => {
    setPendingInserts((list || []).map((d) => ({
      key: d.key,
      sql: buildInsertPreview({ database: props.database, table: props.table, columns: d.values }),
    })));
  };

  const handleEditPreview = (rowIdx, changes, kind, meta) => {
    if (rowIdx == null || !changes || Object.keys(changes).length === 0) { setEditPreview(''); return; }
    if (kind === 'insert') { setEditPreview(''); return; } // shown in the pending-inserts list
    const row = meta?.row || rows()?.rows?.[rowIdx];
    if (!row) { setEditPreview(''); return; }
    const cols = rows()?.columns ?? [];
    const pkCols = pkColumnNames();
    const pkVals = pkCols.map((p) => row[cols.findIndex((c) => c.name === p)]);
    setEditPreview(buildUpdatePreview({
      database: props.database, table: props.table,
      pkColumns: pkCols, pkValues: pkVals, setColumns: changes,
    }));
  };

  // ── New-row insert (Phase 1) ──────────────────────────────────────────────
  // On-error behaviour for row writes when committing: 'stop' rolls the whole
  // transaction back on the first failure; 'skip' rolls back only the failing
  // row (SAVEPOINT) and keeps the rest. The Grid exposes its draft controls via
  // these registered callbacks.
  const [editError, setEditError] = createSignal('stop');
  let rowInserter = null;     // appendDraft()
  let flushDraftsFn = null;   // flushDrafts() → { validation, aborted }
  let clearDraftsFn = null;   // clearDrafts()
  let clearDirtyFn = null;    // clear staged-edit markers
  const registerRowInserter   = (fn) => { rowInserter = fn; };
  const registerInsertFlusher = (fn) => { flushDraftsFn = fn; };
  const registerDraftClearer  = (fn) => { clearDraftsFn = fn; };
  const registerDirtyClearer  = (fn) => { clearDirtyFn = fn; };
  const [dirtyCount, setDirtyCount] = createSignal(0);

  // ── Gutter row selection (for bulk DELETE in edit mode) ────────────────
  // The Grid emits the current RAW indices of the selected rows; we keep a
  // local mirror so the toolbar can show "🗑 Delete N row(s)". Indices are
  // re-derived after every rows/view change because we hand Grid a getRowKey:
  // selection is stored by PK tuple, so local sort/filter doesn't shuffle
  // selected rows into "selected by accident."
  const [selectedRowIdxs, setSelectedRowIdxs] = createSignal([]);
  let clearGridRowSelection = null;
  const registerRowSelectionClearer = (fn) => { clearGridRowSelection = fn; };
  // PK tuple → stable key for selection storage. The unit-separator char is
  // vanishingly unlikely in real PK values, so it doesn't collide with the
  // joined value of a multi-column key.
  const rowKeyOf = (rowIdx) => {
    const cur = rows();
    const row = cur?.rows?.[rowIdx];
    if (!row) return null;
    const cols = cur.columns ?? [];
    const pkCols = pkColumnNames();
    if (!pkCols.length) return null;
    const parts = [];
    for (const p of pkCols) {
      const ci = cols.findIndex((c) => c.name === p);
      parts.push(ci >= 0 ? String(row[ci]) : '');
    }
    return parts.join('\x1f');
  };

  // Grid → us, per draft, at commit-flush. Sends the INSERT and returns the
  // verdict the Grid uses to keep / drop / stop on the draft.
  const handleRowInsert = async (values) => {
    if (!editing()) return 'aborted';
    try { await ensureEditSession(); }
    catch (e) { logPush({ sql: 'BEGIN (failed)', ok: false, error: e.message }); return 'aborted'; }
    try {
      const r = await withEditSession((sid) => insertRow(sid, {
        database: props.database, table: props.table,
        columns: writeValues(values, colTypeOf), onError: editError(),
      }));
      if (r.error) {
        logPush({ sql: r.sql, ok: false, error: r.error, rowsAffected: 0, warnings: r.warnings });
        if (r.autoRolledBack) { setInTx(false); return 'aborted'; } // stop mode → whole tx gone
        return 'skipped'; // skip mode → savepoint rolled back, tx alive
      }
      logPush({ sql: r.sql, ok: true, rowsAffected: r.rowsAffected, warnings: r.warnings });
      return 'ok';
    } catch (e) {
      if (handleEditSessionGone(e, 'new row')) return 'aborted';
      logPush({ sql: `INSERT INTO ${props.database}.${props.table} (failed before send)`, ok: false, error: e.message });
      return editError() === 'skip' ? 'skipped' : 'aborted';
    }
  };

  // Reset page (and sort) when table or filters change — also when the page size
  // (row limit) changes, so offsets stay valid.
  createEffect(() => {
    props.table; props.database;
    filters();
    pageSize();
    setPage(0);
  });
  createEffect(() => { props.table; props.database; setSortKeys([]); });

  const [rows, { refetch: refetchRows, mutate: mutateRows }] = createResource(
    () => [props.connectionId, props.database, props.table, page(), filters(), sortKeys(), pageSize()],
    async ([id, db, table, p, filt, srt, lim]) => {
      try {
        let url = `/api/connections/${id}/databases/${encodeURIComponent(db)}/tables/${encodeURIComponent(table)}/rows?offset=${p * lim}&limit=${lim}`;
        if (filt.length > 0) {
          url += `&filter=${encodeURIComponent(JSON.stringify(filt))}`;
        }
        if (srt.length > 0) {
          url += `&sort=${encodeURIComponent(JSON.stringify(srt))}`;
        }
        return await api.get(url);
      } catch (e) {
        toast.error(`Load rows: ${e.message}`);
        return { columns: [], rows: [], total: 0 };
      }
    }
  );

  // Sort-icon click handler — see lib/gridview.js::cycleSort for the cycle rules
  // (shared with the query-result grid). Re-queries via the sortKeys() resource dep.
  const toggleSort = async (colName, ctrlKey) => {
    if (!(await beforeViewChange())) return;
    setSortKeys(cycleSort(sortKeys(), colName, ctrlKey));
  };
  const goToPage = async (p) => { if (await beforeViewChange()) setPage(p); };

  // ── Edit-mode lifecycle ──
  const pkColumnNames = () => (rows()?.columns ?? []).filter((c) => c.isPrimary).map((c) => c.name);
  const tableHasPK    = () => pkColumnNames().length > 0;

  // ── Edit-behaviour prefs (per-user, both default on = classic behaviour) ──
  //   editRequireMode  off → cells are directly editable (no "Edit" click gate).
  //   editInTransaction off → each write auto-commits per row (no Commit/Rollback).
  const editing = () => editMode() || !editRequireMode();
  const txMode  = () => editInTransaction();
  // Lazily ensure a pinned edit session exists and (in tx mode) a transaction is
  // open. Called by enterEditMode AND by every write, so directly-editable mode
  // (no explicit enter) still gets a session on its first change without holding
  // one open for tabs you never touch.
  const ensureEditSession = async () => {
    let sid = sessionId();
    if (!sid) {
      const s = await createQuerySession(props.connectionId, props.database);
      sid = s.sessionId;
      setSessionId(sid);
    }
    if (txMode() && !inTx()) {
      await beginTransaction(sid);
      setInTx(true);
      logPush({ sql: 'BEGIN', ok: true });
    }
    return sid;
  };

  // Column type by name (for exact key / BIT values on the wire).
  const colTypeOf = (name) => (rows()?.columns ?? []).find((c) => c.name === name)?.type;

  // Run an edit request on the pinned edit session. In auto-commit mode nothing
  // is held between writes, so a session reaped while idle (404 — nothing ran)
  // is reopened and the request retried transparently: edit mode and pending
  // new rows survive. In a transaction the 404 propagates — the transaction is
  // gone, so the caller ends edit mode and says so.
  const withEditSession = async (fn) => {
    const sid = await ensureEditSession();
    try {
      return await fn(sid);
    } catch (e) {
      if (e?.status !== 404 || txMode()) throw e;
      setSessionId(null);
      const sid2 = await ensureEditSession();
      logPush({ sql: '-- the idle edit session had expired: reconnected', ok: true });
      return await fn(sid2);
    }
  };

  // A 404 / 410 on an edit request: the edit session is gone. Says what was
  // lost (an open transaction, a statement whose outcome is unknown) and leaves
  // edit mode. Returns false for any other error.
  const handleEditSessionGone = (e, what) => {
    const info = sessionLostInfo(e);
    if (!info && e?.status !== 404) return false;
    handleEditSessionLost(info
      ? sessionLostMessage(info, what)
      : inTx()
        ? 'The edit session was closed — its open transaction was rolled back; uncommitted changes are gone.'
        : 'The edit session was closed.');
    return true;
  };

  const enterEditMode = async () => {
    if (editMode() || !tableHasPK()) return;
    try {
      await ensureEditSession();
      setEditMode(true);
    } catch (e) {
      toast.error(`Edit mode failed: ${e.message}`);
      const sid = sessionId();
      if (sid) { try { await closeQuerySession(sid); } catch (_) {} setSessionId(null); }
      setInTx(false);
    }
  };

  // Tear down edit mode once the transaction has already ended (a user commit /
  // rollback, or the exit-time rollback below): close the pinned edit session and
  // reset all edit state. refetchRows reloads via a normal read so the grid shows
  // the committed truth.
  const leaveEditMode = async (sid) => {
    if (sid) { try { await closeQuerySession(sid); } catch (_) {} }
    setSessionId(null);
    setInTx(false);
    setEditMode(false);
    setEditPreview('');
    clearDraftsFn?.();
    clearDirtyFn?.();
    setSqlLog([]);
    refetchRows();
  };

  const exitEditMode = async () => {
    if (!editMode()) return;
    const sid = sessionId();
    if (flushGrid) await flushGrid();
    if (sid && inTx()) {
      try { await rollbackTransaction(sid); logPush({ sql: 'ROLLBACK (exit)', ok: true }); }
      catch (_) {}
    }
    await leaveEditMode(sid);
  };

  // In transaction mode the idle reaper never evicts the edit session; a 404
  // ("session not found") means it was killed from the Sessions panel, and a 410
  // that its connection died. Reconnecting + retrying a write would run it
  // OUTSIDE the original transaction, so instead we exit edit mode cleanly, say
  // what was lost, and ask the user to re-enter — never silently retry. (Auto-
  // commit mode reconnects transparently instead — see withEditSession.)
  const handleEditSessionLost = (msg) => {
    const n = pendingInserts().length;
    toast.error(`${msg}${n ? ` ${n} unsaved new row${n === 1 ? ' was' : 's were'} discarded.` : ''} Re-enter edit mode to continue.`);
    const sid = sessionId();
    if (sid) closeQuerySession(sid).catch(() => {});
    setSessionId(null);
    setInTx(false);
    setEditMode(false);
    setEditPreview('');
    clearDraftsFn?.();
    setSqlLog([]);
    refetchRows();
  };

  const commitEdit = async () => {
    const sid = sessionId();
    if (!sid || !inTx()) return;
    try {
      if (flushGrid) await flushGrid();
      // Send pending new-row drafts as INSERTs before committing.
      if (flushDraftsFn) {
        const res = await flushDraftsFn();
        if (res?.validation?.length) {
          toast.error('A new row is missing a required value — fill it (or remove the row) before committing.');
          return; // leave the transaction open so nothing is lost
        }
        if (res?.aborted) {
          // Stop-on-error: the backend rolled the whole transaction back.
          setInTx(false);
          clearDraftsFn?.();
          try { await beginTransaction(sid); setInTx(true); logPush({ sql: 'BEGIN (after rollback)', ok: true }); } catch (_) {}
          refetchRows();
          return;
        }
      }
      await commitTransaction(sid);
      logPush({ sql: 'COMMIT', ok: true });
      await leaveEditMode(sid); // auto-exit edit mode after commit
    } catch (e) {
      if (handleEditSessionGone(e, 'commit')) return;
      logPush({ sql: 'COMMIT', ok: false, error: e.message });
      toast.error(`Commit failed: ${e.message}`);
    }
  };

  const rollbackEdit = async () => {
    const sid = sessionId();
    if (!sid || !inTx()) return;
    try {
      // Pending edits don't need to be POSTed (we're about to ROLLBACK them
      // anyway), but flush so the SQL log is honest about what the user did.
      if (flushGrid) await flushGrid();
      await rollbackTransaction(sid);
      logPush({ sql: 'ROLLBACK', ok: true });
      await leaveEditMode(sid); // auto-exit edit mode after rollback
    } catch (e) {
      if (handleEditSessionGone(e, 'rollback')) return;
      logPush({ sql: 'ROLLBACK', ok: false, error: e.message });
      toast.error(`Rollback failed: ${e.message}`);
    }
  };

  // Autocommit mode has no Commit button, so pending new-row drafts are flushed
  // here — each INSERT auto-commits on its own (existing-cell edits already saved
  // on row-leave).
  const saveDrafts = async () => {
    try {
      if (flushGrid) await flushGrid();
      if (flushDraftsFn) {
        const res = await flushDraftsFn();
        if (res?.validation?.length) {
          toast.error('A new row is missing a required value — fill it (or remove the row) first.');
          return;
        }
      }
      refetchRows();
    } catch (e) {
      if (handleEditSessionGone(e, 'new rows')) return;
      toast.error(`Save failed: ${e.message}`);
    }
  };

  // PK-rename confirmation: shown when a buffered edit changes any PK column
  // on an existing row. Holds the in-flight handleRowEdit awaiting the user's
  // decision; onConfirm resumes the UPDATE, onCancel reports `{ cancelled }`
  // back to Grid so it drops the dirty markers.
  const [pkConfirm, setPKConfirm] = createSignal(null);
  // ^^ shape: { oldPK, newPK, resolve }

  // Grid → us, when the cursor leaves a row that has buffered edits. One POST
  // per row, with every changed column in the same SET clause — matches how
  // Navicat batches.
  const handleRowEdit = async (rowIdx, changes, meta) => {
    if (!editing()) return;
    if (!changes || Object.keys(changes).length === 0) return;
    // The row the edit was made on (the Grid hands it over — rowIdx may be
    // stale if the rows changed meanwhile).
    const row = meta?.row || rows()?.rows?.[rowIdx];
    if (!row) return;
    const pkCols = pkColumnNames();
    const colByIdx = (rows()?.columns ?? []);
    const pkVals = pkCols.map((p) => row[colByIdx.findIndex((c) => c.name === p)]);
    // PK rename gate: any PK column in the SET map → open the FK-aware modal
    // and wait for confirmation. Cancel surfaces `{ cancelled: true }` so the
    // Grid drops the dirty markers we'd otherwise leave behind.
    const changedPKs = pkCols.filter((p) => Object.prototype.hasOwnProperty.call(changes, p));
    if (changedPKs.length > 0) {
      const oldPK = {};
      const newPK = {};
      for (const p of changedPKs) {
        oldPK[p] = row[colByIdx.findIndex((c) => c.name === p)];
        newPK[p] = changes[p];
      }
      const ok = await new Promise((resolve) => setPKConfirm({ oldPK, newPK, resolve }));
      setPKConfirm(null);
      if (!ok) return { cancelled: true };
    }
    try {
      const r = await withEditSession((sid) => updateRow(sid, {
        database: props.database,
        table:    props.table,
        pkColumns: pkCols,
        pkValues:  keyValues(pkVals, pkCols.map(colTypeOf)),
        setColumns: writeValues(changes, colTypeOf),
        onError:   editError(),
      }));
      if (r.error) {
        logPush({ sql: r.sql, ok: false, error: r.error, rowsAffected: 0, warnings: r.warnings });
        if (r.autoRolledBack) {
          setInTx(false);
          try { await beginTransaction(sessionId()); setInTx(true); logPush({ sql: 'BEGIN (after auto-rollback)', ok: true }); }
          catch (_) {}
          refetchAfterWrite('the transaction was rolled back');
        }
        return { cancelled: true }; // nothing was saved — drop the staged-edit markers
      }
      logPush({ sql: r.sql, ok: true, rowsAffected: r.rowsAffected, warnings: r.warnings });
      if (r.warnings?.length) toast.warn('The server raised warnings for that row — it may have stored something other than what you typed (the grid shows the stored values).');
      // Optimistic local mutate — apply every changed column to the row so
      // the grid shows them immediately without a refetch. If a PK column was
      // rewritten, the server-returned newPKValues are the canonical post-
      // UPDATE PK tuple (handles any engine-side coercion / trimming); fall
      // back to the user-typed value if the server didn't surface them. With
      // warnings the server also sends the row as stored (`row`), which wins.
      const cur = rows();
      const ri = cur?.rows ? cur.rows.indexOf(row) : -1;
      if (ri >= 0) {
        const nextRows = cur.rows.slice();
        nextRows[ri] = nextRows[ri].slice();
        for (const [c, v] of Object.entries(changes)) {
          const ci = colByIdx.findIndex((col) => col.name === c);
          if (ci >= 0) nextRows[ri][ci] = v;
        }
        if (Array.isArray(r.newPKValues) && r.newPKValues.length === pkCols.length) {
          for (let i = 0; i < pkCols.length; i++) {
            const ci = colByIdx.findIndex((col) => col.name === pkCols[i]);
            if (ci >= 0) nextRows[ri][ci] = r.newPKValues[i];
          }
        }
        if (r.row && typeof r.row === 'object') {
          for (const [c, v] of Object.entries(r.row)) {
            const ci = colByIdx.findIndex((col) => col.name === c);
            if (ci >= 0) nextRows[ri][ci] = v;
          }
        }
        mutateRows({ ...cur, rows: nextRows });
      }
    } catch (e) {
      if (handleEditSessionGone(e, 'row edit')) return { cancelled: true };
      logPush({ sql: `UPDATE ${props.database}.${props.table} (failed before send)`, ok: false, error: e.message });
      return { cancelled: true };
    }
  };

  // ── Bulk delete the gutter-selected rows ──────────────────────────────────
  // Posts one DELETE per row (PK-based, parameter-bound). Each outcome is
  // echoed to the SQL log so the user sees exactly what ran. Successes are
  // dropped from the local `rows` array optimistically; if "stop" mode hits an
  // error mid-batch the backend auto-rolls back and we restart the tx like
  // handleRowEdit does. Selection is cleared on success regardless of how many
  // rows survived (the indices are stale after the mutate).
  const handleDeleteSelected = async () => {
    if (!editing()) return;
    const idxs = selectedRowIdxs();
    if (!idxs.length) return;
    const cur = rows();
    if (!cur?.rows) return;
    const cols = cur.columns ?? [];
    const pkCols = pkColumnNames();
    if (!pkCols.length) {
      toast.error('Bulk delete needs a primary key — this table has none.');
      return;
    }
    // Accurate consequence: permanent in auto-commit mode, and also inside a
    // transaction when the table's engine can't roll back (MyISAM, MEMORY…).
    if (!window.confirm(deleteConfirmMessage(idxs.length, { inTransaction: txMode(), engine: cur.engine }))) {
      return;
    }
    // Flush any in-progress row edit FIRST so the buffered UPDATE doesn't
    // arrive after the DELETE (which would error 0-affected and surprise the user).
    if (flushGrid) await flushGrid();
    // Build per-row PK tuples; skip any indices that point at a stale row.
    const pkColIdxs = pkCols.map((p) => cols.findIndex((c) => c.name === p));
    const validIdxs = [];
    const pkRows = [];
    for (const i of idxs) {
      const row = cur.rows[i];
      if (!row) continue;
      validIdxs.push(i);
      pkRows.push(pkColIdxs.map((ci) => (ci >= 0 ? row[ci] : null)));
    }
    if (!pkRows.length) return;
    const pkTypes = pkCols.map(colTypeOf);
    try {
      const r = await withEditSession((sid) => deleteRows(sid, {
        database: props.database,
        table: props.table,
        pkColumns: pkCols,
        rows: pkRows.map((vals) => keyValues(vals, pkTypes)),
        onError: editError(),
      }));
      // Echo every per-row outcome (ok or error) to the SQL log.
      if (Array.isArray(r.outcomes)) {
        for (const oc of r.outcomes) {
          if (oc.error) {
            logPush({ sql: oc.sql || '(DELETE)', ok: false, error: oc.error, rowsAffected: oc.rowsAffected || 0 });
          } else {
            logPush({ sql: oc.sql, ok: true, rowsAffected: oc.rowsAffected, warnings: oc.warnings });
          }
        }
      }
      // If the whole tx was auto-rolled back (stop mode hit an error), reset
      // local state and start a fresh tx so the editor stays usable.
      if (r.autoRolledBack) {
        setInTx(false);
        try { await beginTransaction(sessionId()); setInTx(true); logPush({ sql: 'BEGIN (after auto-rollback)', ok: true }); }
        catch (_) {}
        refetchAfterWrite('the transaction was rolled back');
        clearGridRowSelection?.();
        return;
      }
      // Optimistic mutate: drop the successfully-deleted rows from the local
      // array. A row is "succeeded" if its outcome has no error AND wasn't
      // savepoint-rolled-back. Outcomes are in the same order as validIdxs.
      const drop = new Set();
      const outcomes = r.outcomes || [];
      for (let i = 0; i < validIdxs.length && i < outcomes.length; i++) {
        const oc = outcomes[i];
        if (!oc.error && !oc.savepointRolledBack) drop.add(validIdxs[i]);
      }
      if (drop.size) {
        const nextRows = cur.rows.filter((_, i) => !drop.has(i));
        mutateRows({ ...cur, rows: nextRows });
      }
      clearGridRowSelection?.();
      if (r.error) {
        toast.error(`Delete stopped: ${r.error}`);
      } else if (r.succeededRows < r.totalRows) {
        toast.error(`Deleted ${r.succeededRows}/${r.totalRows} — see SQL log for the failures.`);
      } else {
        toast.success(`Deleted ${r.succeededRows} row${r.succeededRows === 1 ? '' : 's'}`);
      }
    } catch (e) {
      if (handleEditSessionGone(e, 'delete')) return;
      logPush({ sql: `DELETE ${props.database}.${props.table} (failed before send)`, ok: false, error: e.message });
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  // ── Reloading rows while editing ──────────────────────────────────────────
  // Reloading the rows (page / sort / filter / limit change, Refresh) remounts
  // the Grid, which used to drop a row edit not yet sent and every unsaved new
  // row without a word. So first send the pending row edit — exactly what
  // moving to another row does — and ask before discarding unsaved new rows.
  const beforeViewChange = async () => {
    if (!editing()) return true;
    if (flushGrid) { try { await flushGrid(); } catch (_) {} }
    const n = pendingInserts().length;
    if (n > 0 && !window.confirm(`${n} new row${n === 1 ? '' : 's'} not saved yet — reloading the rows discards ${n === 1 ? 'it' : 'them'}. Continue?`)) {
      return false;
    }
    return true;
  };
  // A reload forced by the server (after it rolled the transaction back).
  const refetchAfterWrite = (why) => {
    const n = pendingInserts().length;
    if (n) toast.warn(`${n} unsaved new row${n === 1 ? ' was' : 's were'} discarded — ${why}.`);
    refetchRows();
  };

  // Grid registers a flusher with us on mount so Commit/Rollback can ensure
  // any in-progress row gets POSTed before the transactional decision lands.
  let flushGrid = null;
  const registerGridFlusher = (fn) => { flushGrid = fn; };

  // Grid registers its auto-fit measurer on mount. "Auto-fit" is a one-shot
  // action: compute content-fit widths and persist them through the same path
  // as a manual resize (so it never fights later manual drags — it IS one).
  let gridAutoFit = null;
  const registerGridAutoFit = (fn) => { gridAutoFit = fn; };
  const handleAutoFit = () => {
    if (!gridAutoFit) return;
    persist(snapshot({ widths: gridAutoFit() }));
  };

  // Splitter — drag the bar between grid and SQL log.
  const startLogSplitterDrag = (e) => {
    e.preventDefault();
    const host = splitHostEl;
    if (!host) return;
    const move = (ev) => {
      const rect = host.getBoundingClientRect();
      if (rect.height <= 0) return;
      const ratio = (ev.clientY - rect.top) / rect.height;
      setLogSplit(Math.min(0.9, Math.max(0.2, ratio)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Close session if the panel unmounts while still in edit mode.
  onCleanup(() => {
    const sid = sessionId();
    if (sid) closeQuerySession(sid);
  });

  const onHeaderClick = async (col, e) => {
    const rect = e.currentTarget.getBoundingClientRect(); // capture before the await
    const prefill = await readClipboardPrefill();
    setEditIndex(null);
    setPrefillValue(prefill);
    // A clipboard-prefilled value defaults to "contains" (the usual ad-hoc lookup);
    // an empty open leaves it undefined so the popover's own "contains" default wins.
    setPrefillOp(prefill ? 'like' : undefined);
    setPopoverRect(rect);
    setPopoverCol(col.name);
  };

  // Click a filter chip to edit it: reopen the popover prefilled with its op+value.
  const editFilter = (idx, e) => {
    const f = filters()[idx];
    if (!f) return;
    setEditIndex(idx);
    setPrefillValue(f.value ?? '');
    setPrefillOp(f.op);
    setPopoverRect(e.currentTarget.getBoundingClientRect());
    setPopoverCol(f.column);
  };

  // Apply the popover: append a new filter, or replace the one being edited
  // (preserving its AND/OR connector).
  const applyFilter = async (f) => {
    if (!(await beforeViewChange())) return;
    const idx = editIndex();
    if (idx == null) {
      props.onSetFilters?.([...filters(), { ...f, connector: 'AND' }]);
    } else {
      props.onSetFilters?.(filters().map((old, i) => (i === idx ? { ...f, connector: old.connector } : old)));
    }
    setPopoverCol(null);
    setEditIndex(null);
  };

  const removeFilter = async (idx) => {
    if (!(await beforeViewChange())) return;
    props.onSetFilters?.(filters().filter((_, i) => i !== idx));
  };

  // Change the AND/OR connector between filter idx and the previous one.
  // idx must be > 0 (the first filter has no connector).
  const setConnector = async (idx, value) => {
    if (idx <= 0) return;
    if (!(await beforeViewChange())) return;
    const next = filters().map((f, i) => (i === idx ? { ...f, connector: value } : f));
    props.onSetFilters?.(next);
  };

  const clearAll = async () => { if (await beforeViewChange()) props.onSetFilters?.([]); };

  const isFiltered = () => filters().length > 0;

  // Pager, rendered in the grid footer so the toolbar keeps its width. The
  // table-wide row estimate means nothing for a filtered view, so it's dropped.
  const pageInfo = createMemo(() =>
    pageRange(page(), pageSize(), rows()?.rows?.length || 0, isFiltered() ? null : (rows()?.total ?? null)));
  const pagerFooter = () => (
    <div class="pager">
      <Show when={pageInfo().hasPrev || pageInfo().hasNext}>
        <button class="btn btn-ghost" disabled={!pageInfo().hasPrev || rows.loading}
                onClick={() => goToPage(Math.max(0, page() - 1))}>‹ Prev</button>
      </Show>
      <span class="grid-footer-rows">
        {pageInfo().label}
        <Show when={isFiltered()}> <span class="grid-footer-dim">(filtered)</span></Show>
      </span>
      <Show when={pageInfo().hasPrev || pageInfo().hasNext}>
        <button class="btn btn-ghost" disabled={!pageInfo().hasNext || rows.loading}
                onClick={() => goToPage(page() + 1)}>Next ›</button>
      </Show>
    </div>
  );

  // PK helpers.
  const cols    = () => rows()?.columns ?? [];
  const pkCount = createMemo(() => cols().filter((c) => c.isPrimary).length);
  const hasPK   = () => pkCount() > 0;

  // ── Column prefs: width / position / pinned, persisted in SQLite ──
  // Loaded once per (conn, db, table), then mutated optimistically.
  const [colPrefs, { mutate: mutateColPrefs, refetch: refetchColPrefs }] = createResource(
    () => [props.connectionId, props.database, props.table],
    ([id, db, table]) => loadColumnPrefs(id, db, table)
  );

  // Build the *effective* per-column state by merging saved prefs with defaults
  // for any column not yet touched by the user.
  // Returns: [{ name, width, position, pinned, excludeFormat, isPrimary, naturalIdx }, ...]
  // sorted by `position` ascending. New unsaved columns go to the end.
  //
  // Memoized — recomputes only when cols/colPrefs/gridShowTypes actually change.
  // Without this it ran on every render for every dependent accessor
  // (displayOrder, widthByName, pinnedNames, excludedFormatNames, snapshot, …).
  //
  // Width fallback uses `undefined` so Grid applies its own default-width
  // logic (which tracks the Types toggle internally) — single source of truth.
  const effective = createMemo(() => {
    const c = cols();
    if (c.length === 0) return [];
    const saved = colPrefs() ?? [];
    const byName = new Map(saved.map((p) => [p.columnName, p]));
    const maxSavedPos = saved.reduce((m, p) => Math.max(m, p.position), -1);

    return c
      .map((col, naturalIdx) => {
        const p = byName.get(col.name);
        return {
          name:          col.name,
          isPrimary:     col.isPrimary,
          type:          col.type,
          naturalIdx,
          width:         p?.width         ?? (gridShowTypes() ? 180 : 130),
          position:      p?.position      ?? (maxSavedPos + 1 + naturalIdx),
          pinned:        p?.pinned        ?? false,
          excludeFormat: p?.excludeFormat ?? false,
          hidden:        p?.hidden        ?? false,
        };
      })
      .sort((a, b) => a.position - b.position);
  });

  // Display order = pinned section first (PKs then user-pinned), rest after.
  // Pin only applies when the global Pin PK toggle is on.
  // Hidden columns are dropped entirely unless the "Show hidden" toggle is on,
  // in which case they stay in place (Grid dims them so they're recognisable).
  const displayOrder = () => {
    const showHidden = gridShowHidden();
    const eff = effective().filter((c) => showHidden || !c.hidden);
    if (!gridPinPK() || !hasPK()) {
      return eff.map((c) => c.name);
    }
    const pks    = eff.filter((c) => c.isPrimary).map((c) => c.name);
    const extras = eff.filter((c) => c.pinned && !c.isPrimary).map((c) => c.name);
    const pinned = [...pks, ...extras];
    const pinnedSet = new Set(pinned);
    const rest = eff.filter((c) => !pinnedSet.has(c.name)).map((c) => c.name);
    return [...pinned, ...rest];
  };

  // Hidden column names (for Grid's dimmed visual cue) + count (for the toolbar).
  const hiddenNames = () => effective().filter((c) => c.hidden).map((c) => c.name);
  const hiddenCount = () => effective().reduce((n, c) => n + (c.hidden ? 1 : 0), 0);
  const visibleCount = () => effective().length - hiddenCount();

  const widthByName = () => {
    const out = {};
    for (const c of effective()) out[c.name] = c.width;
    return out;
  };

  const pinnedNames = () => {
    if (!gridPinPK() || !hasPK()) return [];
    return effective()
      .filter((c) => c.isPrimary || c.pinned)
      .map((c) => c.name);
  };

  // Column names opted-OUT of thousand-separator formatting.
  const excludedFormatNames = () =>
    effective().filter((c) => c.excludeFormat).map((c) => c.name);

  const hasCustomPrefs = () => (colPrefs() ?? []).length > 0;

  // Persist the current effective snapshot to the backend (debounced is
  // unnecessary — commits only fire on mouseup / drop / right-click toggle).
  const persist = (next) => {
    mutateColPrefs(next);
    saveColumnPrefs(props.connectionId, props.database, props.table, next);
  };

  // Build a complete snapshot for saving — every column with explicit values.
  const snapshot = (overrides = {}) => {
    const eff = effective();
    // Reassign positions 0..N-1 in current sorted order so saved positions
    // stay dense and unambiguous after any reorder.
    return eff.map((c, i) => ({
      columnName:    c.name,
      width:         overrides.widths?.[c.name]         ?? c.width,
      position:      overrides.positions?.[c.name]      ?? i,
      pinned:        overrides.pinned?.[c.name]         ?? c.pinned,
      excludeFormat: overrides.excludeFormat?.[c.name]  ?? c.excludeFormat,
      hidden:        overrides.hidden?.[c.name]         ?? c.hidden,
    }));
  };

  // Resize commit handler (called from Grid on mouseup).
  const handleResize = (colName, newWidth) => {
    persist(snapshot({ widths: { [colName]: newWidth } }));
  };

  // Reorder commit handler (called from Grid on drop). Receives the new
  // display order (names). We assign 0..N-1 positions from that order — but
  // remember pinned columns are at the start of display order and shouldn't
  // affect the natural order under the hood. To keep things predictable, we
  // map display order back to "natural order" by removing pinned-section
  // entries from the front of the new order and merging with the existing
  // pinned columns at their saved positions.
  const handleReorder = (newDisplayOrder) => {
    // newDisplayOrder includes pinned columns at the front (unchanged) and
    // unpinned in the user-chosen sequence. We just save positions in the
    // order they appear — pinned columns get low positions, but since pin
    // applies *on top of* natural order, that's correct.
    const positions = {};
    newDisplayOrder.forEach((n, i) => { positions[n] = i; });
    persist(snapshot({ positions }));
  };

  // ── Right-click context menu ────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = createSignal(null); // { x, y, col }

  // "Edit table" — same action as the table-list right-click. Reachable from
  // the header menu and from a right-click anywhere in the data rows.
  const editTableItem = () => ({
    label: 'Edit table', icon: '✎', onClick: () => props.onOpenTableEdit?.(),
  });

  // Data-cell menu items (table-level only). Shared by the edit-mode right-click
  // (onCellContextMenu) and the browse-mode merged copy menu (extraCellItems).
  const cellMenuItems = () => [editTableItem()];

  // ── Foreign-key navigation ──────────────────────────────────────────────────
  // FK metadata for THIS table comes from the shared schema cache (loaded on
  // mount). A right-clicked cell in an FK column with a non-null value gets a
  // "Go to <parent>" item that opens the referenced row (refCol = value) in a
  // data tab.
  onMount(() => loadCompletionSchema(props.connectionId));
  const tableForeignKeys = () => {
    const dbs = completionState(props.connectionId)().databases || [];
    const db = dbs.find((d) => d.database === props.database);
    if (!db) return [];
    const t = (db.tables || []).find((x) => x.name === props.table)
           || (db.views || []).find((x) => x.name === props.table);
    return t?.foreignKeys || [];
  };
  // The FK whose local columns include `name` (or null) + its referenced column.
  const fkFor = (name) => tableForeignKeys().find((f) => (f.columns || []).includes(name)) || null;
  const isFkColumn = (name) => !!fkFor(name);
  const refColFor = (fk, name) => (fk.refCols || [])[(fk.columns || []).indexOf(name)] || (fk.refCols || [])[0] || '';
  const cellVal = (name, rowIdx) => {
    const ci = (rows()?.columns || []).findIndex((c) => c.name === name);
    return ci >= 0 ? rows()?.rows?.[rowIdx]?.[ci] : null;
  };
  // Open the parent row referenced by an FK cell (shared by the menu item + Ctrl+click).
  const navigateFk = (name, rowIdx) => {
    const fk = fkFor(name);
    if (!fk) return;
    const value = cellVal(name, rowIdx);
    if (value == null) return;
    const refDb = fk.refSchema || props.database;
    const refCol = refColFor(fk, name);
    if (!refCol) return;
    props.onOpenTableFiltered?.(refDb, fk.refTable, [{ column: refCol, op: '=', value: String(value) }]);
  };
  // Lazy parent-value suggestions for the edit-time FK lookup datalist: distinct
  // values of the referenced column from up to 500 parent rows (sample — the cell
  // stays free-typeable). Cached per FK column.
  const fkOptCache = {};
  const fetchFkOptions = async (name) => {
    if (fkOptCache[name]) return fkOptCache[name];
    const fk = fkFor(name);
    if (!fk) return [];
    const refDb = fk.refSchema || props.database;
    const refCol = refColFor(fk, name);
    if (!refCol) return [];
    try {
      const r = await api.get(`/api/connections/${props.connectionId}/databases/${encodeURIComponent(refDb)}/tables/${encodeURIComponent(fk.refTable)}/rows?limit=500`);
      const ci = (r?.columns || []).findIndex((c) => c.name === refCol);
      const seen = new Set();
      if (ci >= 0) {
        for (const row of r.rows || []) {
          const v = row[ci];
          if (v == null) continue;
          seen.add(String(v));
          if (seen.size >= 300) break;
        }
      }
      const out = [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      fkOptCache[name] = out;
      return out;
    } catch { return []; }
  };
  const fkMenuItems = (col, rowIdx) => {
    const fk = fkFor(col.name);
    if (!fk) return [];
    const value = cellVal(col.name, rowIdx);
    if (value == null) return []; // NULL references nothing
    const refCol = refColFor(fk, col.name);
    if (!refCol) return [];
    const preview = String(value).length > 24 ? String(value).slice(0, 24) + '…' : String(value);
    return [{
      label: `Go to ${fk.refTable} (${refCol} = ${preview})`,
      icon: '🔗',
      onClick: () => navigateFk(col.name, rowIdx),
    }];
  };

  // Right-click on a data row cell (not the header) → table-level menu only.
  const onCellContextMenu = (col, e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, items: cellMenuItems() });
  };

  // Build the column header menu (pin/hide/move/edit-table). Pure — shared by the
  // edit-mode right-click (onHeaderContextMenu) and the browse-mode merged copy
  // menu (extraHeaderItems), so both modes expose the same column actions.
  const headerMenuItems = (col) => {
    const eff = effective().find((c) => c.name === col.name);
    if (!eff) return [];
    const numeric = isNumericType(col.type);

    // Build the list of items, conditional on what makes sense in context.
    const items = [];

    // Pin / unpin (only meaningful when Pin PK toggle is on, and not for PK cols)
    if (gridPinPK() && hasPK() && !col.isPrimary) {
      items.push(eff.pinned
        ? { label: `Unpin column "${col.name}"`, icon: '📌', onClick: () => togglePin(col, false) }
        : { label: `Pin column "${col.name}"`,   icon: '📍', onClick: () => togglePin(col, true) }
      );
    }

    // Exclude / include from thousand-separator formatting
    if (numeric && gridThousandSep()) {
      if (items.length > 0) items.push({ divider: true });
      items.push(eff.excludeFormat
        ? { label: 'Include in thousand separator',  icon: '#', onClick: () => toggleExcludeFormat(col, false) }
        : { label: 'Exclude from thousand separator', icon: 'Ø', onClick: () => toggleExcludeFormat(col, true) }
      );
    }

    // Move to start — leftmost column. Hidden when already first in natural order.
    if (effective()[0]?.name !== col.name) {
      if (items.length > 0) items.push({ divider: true });
      items.push({ label: 'Move to start', icon: '⇤', onClick: () => moveColumnToStart(col) });
    }

    // Hide / show (frontend-only). "Hide" is suppressed for the last remaining
    // visible column so the grid can't be emptied. "Show" only appears for a
    // column that is currently hidden (reachable via the "Show hidden" toggle).
    if (eff.hidden) {
      if (items.length > 0) items.push({ divider: true });
      items.push({ label: 'Show column', icon: '👁', onClick: () => toggleHidden(col, false) });
    } else if (visibleCount() > 1) {
      if (items.length > 0) items.push({ divider: true });
      items.push({ label: 'Hide column', icon: '🚫', onClick: () => toggleHidden(col, true) });
    }

    // Bulk reveal — offered whenever it isn't just a duplicate of the single
    // "Show column" above (i.e. skip when the only hidden column is this one).
    if (hiddenCount() > 1 || (hiddenCount() === 1 && !eff.hidden)) {
      items.push({ label: 'Show all hidden columns', icon: '👁', onClick: showAllHidden });
    }

    // Edit table — always available, separated from the column-specific items.
    if (items.length > 0) items.push({ divider: true });
    items.push(editTableItem());

    return items;
  };

  // Edit-mode right-click on a header → the column menu in Browser's own popup.
  const onHeaderContextMenu = (col, e) => {
    e.preventDefault();
    const items = headerMenuItems(col);
    if (items.length) setCtxMenu({ x: e.clientX, y: e.clientY, items });
  };

  const togglePin = (col, nowPinned) => {
    persist(snapshot({ pinned: { [col.name]: nowPinned } }));
    toast.info(nowPinned ? `Pinned column "${col.name}"` : `Unpinned column "${col.name}"`);
  };

  const toggleExcludeFormat = (col, nowExcluded) => {
    persist(snapshot({ excludeFormat: { [col.name]: nowExcluded } }));
    toast.info(nowExcluded
      ? `"${col.name}" will not use thousand separator`
      : `"${col.name}" will now use thousand separator`);
  };

  // Hide / show a column. Frontend-only — the real DB column is untouched.
  // Hiding the last visible column is blocked at the menu-build level so the
  // grid never ends up empty.
  const toggleHidden = (col, nowHidden) => {
    persist(snapshot({ hidden: { [col.name]: nowHidden } }));
    // Reveal the toggle's effect immediately: if the user just hid a column
    // while "Show hidden" was on, it stays visible-but-dimmed; if off, it
    // vanishes. Either way the toolbar toggle now reflects the new count.
    toast.info(nowHidden ? `Hid column "${col.name}"` : `Showing column "${col.name}"`);
  };

  // Reveal every hidden column on this table in one go (frontend-only).
  const showAllHidden = () => {
    const hidden = {};
    for (const c of effective()) if (c.hidden) hidden[c.name] = false;
    if (Object.keys(hidden).length === 0) return;
    const n = Object.keys(hidden).length;
    persist(snapshot({ hidden }));
    toast.info(n === 1 ? 'Showing 1 hidden column' : `Showing all ${n} hidden columns`);
  };

  // Move a column to the front of the natural order (leftmost) — a shortcut
  // for dragging it all the way left. Rebuild the order with this column
  // first, then let snapshot() densely reassign positions 0..N-1. Operates on
  // natural order, so pinned columns still render ahead of it when Pin PK is on.
  const moveColumnToStart = (col) => {
    const rest = effective().map((c) => c.name).filter((n) => n !== col.name);
    const positions = {};
    [col.name, ...rest].forEach((n, i) => { positions[n] = i; });
    persist(snapshot({ positions }));
    toast.info(`Moved "${col.name}" to the start`);
  };

  // Reset — wipes saved prefs for this table and re-applies defaults.
  const handleResetColumns = async () => {
    await resetColumnPrefs(props.connectionId, props.database, props.table);
    mutateColPrefs([]);
    toast.info('Column layout reset to defaults');
  };

  return (
    <div class={`table-data-panel ${inTx() ? 'in-tx' : ''}`}>
      {/* Breadcrumb on its own row so the action toolbar below has the full
          horizontal width for its buttons (the pager lives in the grid footer). */}
      <div class="browser-breadcrumb-row">
        <div class="breadcrumb mono">
          <span class="muted">{props.connectionName}</span>
          <span class="sep">/</span>
          <span>{props.database}</span>
          <span class="sep">/</span>
          <span class="strong">{props.table}</span>
        </div>
        <Show when={currentUser()?.role === 'readonly'}>
          <span class="badge-readonly-mode">Read-only</span>
        </Show>
      </div>

      <div class="browser-toolbar">
        {/* Row limit (Navicat-style): checkbox + value cap how many rows load per
            page. Unchecked → load up to the server cap. Persisted per user. */}
        <label class="grid-limit" title="Limit how many rows load per page (uncheck to load up to the maximum)">
          <input type="checkbox" checked={gridLimitEnabled()}
                 onChange={async (e) => {
                   const el = e.currentTarget, v = el.checked;
                   if (await beforeViewChange()) setGridLimitEnabled(v); else el.checked = gridLimitEnabled();
                 }} />
          Limit
          <input type="number" class="input grid-limit-input" min="1" max={MAX_ROWS}
                 value={gridRowLimit()} disabled={!gridLimitEnabled()}
                 onChange={async (e) => {
                   const el = e.currentTarget, v = el.value;
                   if (await beforeViewChange()) setGridRowLimit(v); else el.value = gridRowLimit();
                 }} />
        </label>

        {/* Grid view-toggles — preferences persist per-user */}
        <button
          class={`btn btn-ghost btn-sm grid-toggle ${gridShowTypes() ? 'on' : ''}`}
          onClick={() => setGridShowTypes(!gridShowTypes())}
          title={gridShowTypes() ? 'Hide column types' : 'Show column types'}
        >
          {gridShowTypes() ? '✓' : '○'} Types
        </button>
        <button
          class={`btn btn-ghost btn-sm grid-toggle ${gridPinPK() ? 'on' : ''}`}
          onClick={() => hasPK() && setGridPinPK(!gridPinPK())}
          disabled={!hasPK()}
          title={hasPK()
            ? (gridPinPK()
                ? 'Unpin primary key columns (tip: right-click any column header to pin it too)'
                : 'Pin primary key columns to the left')
            : 'No primary key on this table'}
        >
          {gridPinPK() ? '📌' : '📍'} Pin PK
          <Show when={gridPinPK() && pinnedNames().length > pkCount()}>
            <span class="grid-toggle-badge"> +{pinnedNames().length - pkCount()}</span>
          </Show>
        </button>

        <button
          class={`btn btn-ghost btn-sm grid-toggle ${gridThousandSep() ? 'on' : ''}`}
          onClick={() => setGridThousandSep(!gridThousandSep())}
          title={gridThousandSep()
            ? 'Numbers shown with thousand separators — click to disable (right-click a numeric column for per-column override)'
            : 'Show numbers with thousand separators (1,234,567.89)'}
        >
          {gridThousandSep() ? '✓' : '○'} 1,000
        </button>

        {/* Show-hidden toggle — only relevant when at least one column is
            hidden. Reveals hidden columns (dimmed) so they can be un-hidden. */}
        <Show when={hiddenCount() > 0}>
          <button
            class={`btn btn-ghost btn-sm grid-toggle ${gridShowHidden() ? 'on' : ''}`}
            onClick={() => setGridShowHidden(!gridShowHidden())}
            title={gridShowHidden()
              ? 'Hidden columns shown (dimmed) — click to hide them again. Right-click a dimmed column to un-hide it.'
              : 'Show hidden columns (right-click any shown column to un-hide it)'}
          >
            {gridShowHidden() ? '👁' : '🚫'} Hidden
            <span class="grid-toggle-badge"> {hiddenCount()}</span>
          </button>
        </Show>

        <button
          class="btn btn-ghost btn-sm grid-toggle"
          onClick={handleAutoFit}
          title="Resize every column to fit its content (90th-percentile width). Mostly-empty columns shrink to the NULL tag. You can still drag any column afterwards."
        >
          ↔ Auto-fit
        </button>

        <button
          class="btn btn-ghost btn-sm grid-toggle"
          onClick={handleResetColumns}
          disabled={!hasCustomPrefs()}
          title={hasCustomPrefs()
            ? 'Restore default column widths, order, and pins for this table'
            : 'No custom column layout to reset'}
        >
          ↺ Reset columns
        </button>

        <ExportMenu
          spec={() => ({
            kind: 'table',
            connectionId: props.connectionId,
            database: props.database,
            table: props.table,
            filters: filters(),
            sorts: sortKeys(),
          })}
          columns={() => rows()?.columns || []}
          rows={() => rows()?.rows || []}
          sqlDump={true}
          disabled={!props.table}
        />

        <button
          class="btn btn-ghost btn-sm grid-toggle"
          onClick={async () => { if (await beforeViewChange()) refetchRows(); }}
          disabled={rows.loading}
          title="Re-run the current query (reload rows from the server)"
        >
          ⟳ Refresh
        </button>

        {/* Edit-mode controls move to their own row below (see td-edit-toolbar). */}
        <Show when={editRequireMode() && !editMode()}>
          <button class="btn btn-sm" onClick={enterEditMode}
                  disabled={rows.loading || !tableHasPK()}
                  title={tableHasPK()
                    ? (txMode() ? 'Enter edit mode — opens a transaction; double-click a cell to edit'
                                : 'Enter edit mode — auto-commit per row (no transaction); double-click a cell to edit')
                    : 'Edit requires a primary key on the table'}>
            ✎ Edit
          </button>
        </Show>
      </div>

      {/* Edit-mode controls on their own row so the main toolbar isn't cramped. */}
      <Show when={editing()}>
        <div class="browser-toolbar td-edit-toolbar">
          <Show when={txMode()} fallback={<span class="td-edit-badge td-edit-badge-auto" title="Auto-commit is on — each change saves immediately (no Rollback).">⚡ auto-commit</span>}>
            <span class="td-edit-badge">in transaction</span>
            <Show when={isTransactionalEngine(rows()?.engine) === false}>
              <span class="td-edit-badge td-edit-badge-auto"
                    title={`${rows()?.engine} doesn't support transactions: every change is saved immediately and Rollback can't undo it.`}>
                ⚠ {rows()?.engine}: no rollback
              </span>
            </Show>
          </Show>
          <Show when={dirtyCount() > 0}>
            <span class="grid-changes-badge" title="Edited cells not yet committed">{dirtyCount()} change{dirtyCount() === 1 ? '' : 's'}</span>
          </Show>
          <Show when={txMode()}>
            <button class="btn btn-sm" onClick={commitEdit} disabled={!inTx()}
                    title="COMMIT — persist all changes in this transaction and exit edit mode">
              ✓ Commit
            </button>
            <button class="btn btn-sm btn-danger" onClick={rollbackEdit} disabled={!inTx()}
                    title="ROLLBACK — discard all changes in this transaction and exit edit mode">
              ↶ Rollback
            </button>
          </Show>
          <Show when={editRequireMode()}>
            <button class="btn btn-sm btn-ghost" onClick={exitEditMode}
                    title={txMode() ? 'Leave edit mode (rolls back the open transaction)' : 'Leave edit mode'}>
              ✕ Done
            </button>
          </Show>
          <span class="query-toolbar-sep" />
          <button class="btn btn-sm btn-ghost" onClick={() => rowInserter && rowInserter()}
                  title="Append a new (empty) row. You can also press Insert to add a row above the current one, or Arrow-Down on the last row.">
            ＋ Append row
          </button>
          <Show when={!txMode() && pendingInserts().length > 0}>
            <button class="btn btn-sm btn-primary" onClick={saveDrafts}
                    title="Insert the pending new row(s). Auto-commit is on, so they're saved immediately.">
              💾 Save {pendingInserts().length} new row{pendingInserts().length === 1 ? '' : 's'}
            </button>
          </Show>
          <Show when={selectedRowIdxs().length > 0}>
            <button class="btn btn-sm btn-danger" onClick={handleDeleteSelected}
                    title={`DELETE the selected rows by PK. ${deleteNote({ inTransaction: txMode(), engine: rows()?.engine })}`}>
              🗑 Delete {selectedRowIdxs().length} row{selectedRowIdxs().length === 1 ? '' : 's'}
            </button>
          </Show>
          <Show when={txMode()}>
            <label class="td-edit-onerror" title="What to do if a row write fails when committing: Stop rolls the whole transaction back; Skip rolls back only the failing row (SAVEPOINT) and keeps the rest.">
              On error:
              <select class="input" value={editError()} onChange={(e) => setEditError(e.target.value)}>
                <option value="stop">Stop &amp; roll back</option>
                <option value="skip">Skip &amp; continue</option>
              </select>
            </label>
          </Show>
        </div>
      </Show>

      {/* Filter chip strip — per-pair AND/OR connectors between chips */}
      <Show when={isFiltered()}>
        <div class="filter-strip">
          <span class="filter-strip-label">Filters:</span>
          <For each={filters()}>
            {(f, i) => (
              <>
                {/* Connector dropdown shown BEFORE every chip except the first */}
                <Show when={i() > 0}>
                  <select
                    class="input filter-conn"
                    value={f.connector || 'AND'}
                    onChange={(e) => setConnector(i(), e.target.value)}
                    title="Logical operator with the previous filter"
                  >
                    <option value="AND" selected={(f.connector || 'AND') === 'AND'}>AND</option>
                    <option value="OR"  selected={f.connector === 'OR'}>OR</option>
                  </select>
                </Show>

                <div class="filter-chip" style="cursor:pointer;" title="Click to edit this filter"
                     onClick={(e) => editFilter(i(), e)}>
                  <span class="filter-chip-col mono">{f.column}</span>
                  <span class="filter-chip-op">{formatOp(f.op)}</span>
                  <Show when={!isNoValueOp(f.op)}>
                    <span class="filter-chip-val mono">{f.value === '' ? '∅' : f.value}</span>
                  </Show>
                  <button class="filter-chip-x" onClick={(e) => { e.stopPropagation(); removeFilter(i()); }} title="Remove filter">×</button>
                </div>
              </>
            )}
          </For>
          <button class="btn btn-ghost btn-sm" onClick={clearAll}>Clear all</button>
          <Show when={filters().length >= 2}>
            <span class="filter-precedence-hint" title="SQL natural precedence: AND binds tighter than OR. e.g. A AND B OR C means (A AND B) OR C">
              ⓘ AND binds tighter than OR
            </span>
          </Show>
        </div>
      </Show>

      {/* Grid + (optional, edit-mode only) splitter + SQL log. The split host
          uses flex-basis on its children to apply the user's saved ratio. */}
      <div class="td-grid-and-log" ref={splitHostEl}>
        <div class="grid-wrap"
             style={editMode()
               ? `flex-basis: ${(logSplit() * 100).toFixed(2)}%;`
               : 'flex: 1;'}>
          <Show when={!rows.loading} fallback={
            <div class="muted" style="padding:16px;">Loading rows…</div>
          }>
            <Grid
              columns={rows()?.columns ?? []}
              rows={rows()?.rows ?? []}
              insertTable={props.table}
              startIndex={page() * pageSize()}
              showTypes={gridShowTypes()}
              displayOrder={displayOrder()}
              widthByName={widthByName()}
              pinnedNames={pinnedNames()}
              thousandSep={gridThousandSep()}
              excludedFormat={excludedFormatNames()}
              hiddenNames={hiddenNames()}
              sortKeys={sortKeys()}
              onSortToggle={toggleSort}
              onHeaderClick={onHeaderClick}
              onHeaderContextMenu={onHeaderContextMenu}
              onCellContextMenu={onCellContextMenu}
              selectable={!editing()}
              extraHeaderItems={headerMenuItems}
              extraCellItems={(col, rowIdx) => [...fkMenuItems(col, rowIdx), ...cellMenuItems()]}
              isFkColumn={isFkColumn}
              onFkNavigate={navigateFk}
              fetchFkOptions={fetchFkOptions}
              onResize={handleResize}
              onReorder={handleReorder}
              editable={editing()}
              onRowEdit={handleRowEdit}
              onRowInsert={handleRowInsert}
              onDraftsChange={handleDraftsChange}
              onEditPreview={handleEditPreview}
              registerFlusher={registerGridFlusher}
              registerInsertFlusher={registerInsertFlusher}
              registerRowInserter={registerRowInserter}
              registerDraftClearer={registerDraftClearer}
              registerDirtyClearer={registerDirtyClearer}
              onDirtyChange={setDirtyCount}
              registerAutoFit={registerGridAutoFit}
              onRowSelectionChange={setSelectedRowIdxs}
              registerRowSelectionClearer={registerRowSelectionClearer}
              getRowKey={rowKeyOf}
              footerRows={pagerFooter}
            />
          </Show>
        </div>

        <Show when={editing()}>
          <div class="td-log-splitter" onPointerDown={startLogSplitterDrag}
               title="Drag to resize" />
          <div class="td-sql-log"
               style={`flex-basis: ${((1 - logSplit()) * 100).toFixed(2)}%;`}>
            <div class="td-sql-log-header">
              <span>SQL log</span>
              <span class="muted" style="font-size:11px;margin-left:6px;">
                ({sqlLog().length} statement{sqlLog().length === 1 ? '' : 's'})
              </span>
              <div class="spacer" />
              <Show when={sqlLog().length > 0}>
                <button class="btn btn-sm btn-ghost" onClick={logClear}>Clear</button>
              </Show>
            </div>
            <div class="td-sql-log-body">
              <Show when={editPreview()}>
                <div class="td-sql-log-row td-sql-log-pending"
                     style="cursor:pointer;"
                     title="The statement this row edit will run when you move off the row or Commit (not yet sent). Click to view in full."
                     onClick={() => setShowPreviewModal(true)}>
                  <span class="td-sql-log-time mono">pending</span>
                  <pre class="td-sql-log-text td-sql-log-pending-text mono">{editPreview()}</pre>
                  <span class="query-edit-preview-expand">⤢</span>
                </div>
              </Show>
              {/* Pending new-row INSERTs — all of them, so it's clear they're
                  batched under this transaction (sent together on Commit). */}
              <For each={pendingInserts()}>
                {(p) => (
                  <div class="td-sql-log-row td-sql-log-pending"
                       title="A new row pending — INSERTed on Commit (not yet sent).">
                    <span class="td-sql-log-time mono">pending</span>
                    <pre class="td-sql-log-text td-sql-log-pending-text mono">{p.sql}</pre>
                  </div>
                )}
              </For>
              <Show when={sqlLog().length === 0 && !editPreview() && pendingInserts().length === 0}>
                <div class="muted" style="padding:8px 10px;font-size:12px;">
                  No statements yet — double-click a cell to start editing.
                </div>
              </Show>
              <For each={sqlLog()}>
                {(entry) => (
                  <div class={`td-sql-log-row ${entry.ok ? '' : 'failed'}`}>
                    <span class="td-sql-log-time mono">
                      {new Date(entry.at).toLocaleTimeString()}
                    </span>
                    <pre class="td-sql-log-text mono">{entry.sql}</pre>
                    <Show when={entry.ok && entry.rowsAffected !== undefined}>
                      <span class="muted mono" style="font-size:11px;">
                        — {entry.rowsAffected} row{entry.rowsAffected === 1 ? '' : 's'}
                      </span>
                    </Show>
                    <Show when={!entry.ok && entry.error}>
                      <span class="td-sql-log-error mono">— {entry.error}</span>
                    </Show>
                    {/* Server warnings (a non-strict server truncates / coerces
                        silently) — the stored value may differ from the typed one. */}
                    <Show when={entry.warnings?.length}>
                      <span class="mono" style="font-size:11px;color:var(--warning);"
                            title={entry.warnings.map((w) => `${w.level} ${w.code}: ${w.message}`).join('\n')}>
                        ⚠ {entry.warnings.length} warning{entry.warnings.length === 1 ? '' : 's'}: {entry.warnings[0].message}
                      </span>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>

      <Show when={popoverCol()}>
        <FilterPopover
          column={popoverCol()}
          anchorRect={popoverRect()}
          initialValue={prefillValue()}
          initialOp={prefillOp()}
          onSubmit={applyFilter}
          onCancel={() => { setPopoverCol(null); setEditIndex(null); }}
        />
      </Show>

      <Show when={ctxMenu()}>
        <ContextMenu
          x={ctxMenu().x}
          y={ctxMenu().y}
          items={ctxMenu().items}
          onClose={() => setCtxMenu(null)}
        />
      </Show>

      {/* PK-rename confirmation modal — opened from handleRowEdit when the
          buffered edit changes any PK column. Awaited via a resolver promise so
          the rest of handleRowEdit blocks on the user's choice. */}
      <Show when={pkConfirm()}>
        <PKEditConfirmModal
          open={true}
          connectionId={props.connectionId}
          database={props.database}
          table={props.table}
          oldPK={pkConfirm().oldPK}
          newPK={pkConfirm().newPK}
          onConfirm={() => pkConfirm().resolve(true)}
          onCancel={() => pkConfirm().resolve(false)}
        />
      </Show>

      {/* Pending-statement full view (the one-line preview row opens this) */}
      <Show when={showPreviewModal() && editPreview()}>
        <div class="modal-backdrop" onClick={() => setShowPreviewModal(false)}>
          <div class="modal-card" style="width:min(760px,94vw);" onClick={(e) => e.stopPropagation()}>
            <h3>Pending statement <span class="muted" style="font-size:12px;font-weight:400;">— not yet sent</span></h3>
            <pre class="long-text-view mono">{editPreview()}</pre>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
              <button class="btn btn-ghost" onClick={() => setShowPreviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}

function isNoValueOp(op) {
  return op === 'is_null' || op === 'is_not_null' || op === 'is_empty' || op === 'is_not_empty';
}

// ── DatabaseVisibilityPopover — checkbox list of all DBs ──────────────────────
// Modal-ish overlay (click-outside to close). Edits are buffered locally and
// committed via onSave when the user clicks Apply. Hidden DBs stay visible IN
// THE POPOVER so they can be re-enabled.
function DatabaseVisibilityPopover(props) {
  // Local checkbox state: Map<name, visible>. true = visible (default).
  const [state, setState] = createSignal(new Map(
    props.allDatabases.map((d) => [d.name, !props.hiddenSet.has(d.name)]),
  ));
  const [filter, setFilter] = createSignal('');

  const toggle = (name) => {
    const m = new Map(state());
    m.set(name, !m.get(name));
    setState(m);
  };
  const setAll = (vis) => {
    const m = new Map();
    for (const d of props.allDatabases) m.set(d.name, vis);
    setState(m);
  };

  const apply = () => {
    const hidden = [];
    for (const [name, visible] of state()) if (!visible) hidden.push(name);
    hidden.sort();
    props.onSave(hidden);
    props.onClose();
  };

  const visibleList = () => {
    const f = filter().trim().toLowerCase();
    return props.allDatabases.filter((d) => !f || d.name.toLowerCase().includes(f));
  };

  return (
    <div class="modal-backdrop" onClick={props.onClose}>
      <div class="modal-card db-visibility-card" onClick={(e) => e.stopPropagation()}>
        <h3>Database visibility</h3>
        <p class="muted" style="margin:0 0 10px;font-size:12px;">
          Unchecked databases are hidden from this connection's sidebar tree.
          The databases themselves are not modified on the server.
        </p>
        <input class="input" placeholder="Filter databases…"
               value={filter()} onInput={(e) => setFilter(e.target.value)}
               style="margin-bottom:8px;" />
        <div style="display:flex;gap:8px;margin-bottom:8px;">
          <button class="btn-link" onClick={() => setAll(true)}>Show all</button>
          <button class="btn-link" onClick={() => setAll(false)}>Hide all</button>
        </div>
        <div class="db-visibility-list">
          <For each={visibleList()}>
            {(d) => (
              <label class="db-visibility-row">
                <input type="checkbox"
                       checked={state().get(d.name) !== false}
                       onChange={() => toggle(d.name)} />
                <span class="mono">{d.name}</span>
              </label>
            )}
          </For>
          <Show when={visibleList().length === 0}>
            <div class="muted" style="padding:8px;">No databases match the filter.</div>
          </Show>
        </div>
        <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
          <button class="btn" onClick={apply}>Apply</button>
        </div>
      </div>
    </div>
  );
}
